import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  IndianRupee, AlertTriangle, Wallet,
  Users, Filter, Download, RefreshCw, BarChart3, Landmark, Info, Calendar,
  ChevronDown, X, Layers, Plus, Trash2, Loader2, Zap, Ban, FileText, FileSpreadsheet, CalendarClock,
} from 'lucide-react';
import { SavedFilters } from '../../components/books/FilterKit';
import GlobalSyncPill from './GlobalSyncPill';
import CollectionPlanTrackerModal from './CollectionPlanTrackerModal';
import PaymentsReceivedModal from './PaymentsReceivedModal';
import PaymentsTrendModal from './PaymentsTrendModal';
import VoidPaymentsModal from './VoidPaymentsModal';


// ─── AR aging bucket label parser ─────────────────────────
// Labels come from backend, e.g. "0-30", "31-60", "91+".
// Convert to { min, max } days-overdue bounds for drilldown queries.
const parseBucket = (label) => {
  if (!label || label === 'all') return {};
  if (label.endsWith('+')) return { min: parseInt(label, 10), max: null };
  const [a, b] = label.split('-').map((s) => parseInt(s, 10));
  return { min: a, max: b };
};

// ─── FY helpers ─────────────────────────────────────────────
const fyOf = (d) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);
// Local-date-safe formatter (avoids toISOString UTC-shift bug that broke FY / month boundaries)
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fyRange = (yr) => ({ from: new Date(yr, 3, 1), to: new Date(yr + 1, 2, 31), label: `FY ${String(yr).slice(-2)}-${String(yr + 1).slice(-2)}` });

function computeRange(preset, specificFy) {
  const now = new Date();
  const y = now.getFullYear(); const m = now.getMonth(); const fy = fyOf(now);
  const qi = (mo) => (mo >= 3 && mo <= 5) ? 0 : (mo >= 6 && mo <= 8) ? 1 : (mo >= 9 && mo <= 11) ? 2 : 3;
  const q = qi(m);
  const qStart = q === 0 ? new Date(fy, 3, 1) : q === 1 ? new Date(fy, 6, 1) : q === 2 ? new Date(fy, 9, 1) : new Date(fy + 1, 0, 1);
  const qEnd = q === 0 ? new Date(fy, 5, 30) : q === 1 ? new Date(fy, 8, 30) : q === 2 ? new Date(fy, 11, 31) : new Date(fy + 1, 2, 31);
  const prevQ = q === 0 ? 3 : q - 1; const prevQFy = q === 0 ? fy - 1 : fy;
  const prevQStart = prevQ === 0 ? new Date(prevQFy, 3, 1) : prevQ === 1 ? new Date(prevQFy, 6, 1) : prevQ === 2 ? new Date(prevQFy, 9, 1) : new Date(prevQFy + 1, 0, 1);
  const prevQEnd = prevQ === 0 ? new Date(prevQFy, 5, 30) : prevQ === 1 ? new Date(prevQFy, 8, 30) : prevQ === 2 ? new Date(prevQFy, 11, 31) : new Date(prevQFy + 1, 2, 31);
  const sow = new Date(now); sow.setDate(now.getDate() - now.getDay());
  const pws = new Date(sow); pws.setDate(sow.getDate() - 7);
  const pwe = new Date(sow); pwe.setDate(sow.getDate() - 1);
  switch (preset) {
    case 'today': return { from: now, to: now, label: 'Today' };
    case 'yesterday': { const d = new Date(now); d.setDate(now.getDate() - 1); return { from: d, to: d, label: 'Yesterday' }; }
    case 'this-week': return { from: sow, to: now, label: 'This Week' };
    case 'previous-week': return { from: pws, to: pwe, label: 'Previous Week' };
    case 'this-month': return { from: new Date(y, m, 1), to: new Date(y, m + 1, 0), label: 'This Month' };
    case 'previous-month': return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0), label: 'Previous Month' };
    case 'this-quarter': return { from: qStart, to: qEnd, label: `This Quarter · Q${q + 1} FY ${String(fy).slice(-2)}-${String(fy + 1).slice(-2)}` };
    case 'previous-quarter': return { from: prevQStart, to: prevQEnd, label: `Previous Quarter · Q${prevQ + 1} FY ${String(prevQFy).slice(-2)}-${String(prevQFy + 1).slice(-2)}` };
    case 'this-year': return fyRange(fy);
    case 'previous-year': return { ...fyRange(fy - 1), label: `Previous FY · ${String(fy - 1).slice(-2)}-${String(fy).slice(-2)}` };
    case 'ytd': return { from: new Date(fy, 3, 1), to: now, label: 'YTD (FY)' };
    case 'all-time': return { from: new Date(2020, 0, 1), to: now, label: 'All time' };
    case 'specific-fy': return fyRange(specificFy);
    case 'specific-month': {
      // specificFy here is repurposed as YYYY-MM key (e.g. "2026-07")
      const key = specificFy || `${y}-${String(m + 1).padStart(2, '0')}`;
      const [yy, mm] = String(key).split('-').map(Number);
      const s = new Date(yy, mm - 1, 1); const e = new Date(yy, mm, 0);
      return { from: s, to: e, label: s.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) };
    }
    default: return fyRange(fy);
  }
}

// Build list of months from Apr 2025 → current
function buildMonthList() {
  const out = []; const now = new Date();
  let d = new Date(2025, 3, 1);
  while (d <= now) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ key, label: d.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return out.reverse();
}

// ─── DateRange picker ─────────────────────────────────────
function DateRangePicker({ preset, setPreset, custom, setCustom, specificFy, setSpecificFy, specificMonth, setSpecificMonth }) {
  const range = preset === 'custom' ? { ...custom, label: 'Custom' } : computeRange(preset, preset === 'specific-month' ? specificMonth : specificFy);
  const [open, setOpen] = useState(false);
  const monthList = useMemo(buildMonthList, []);
  const options = [
    { v: 'today', label: 'Today' }, { v: 'yesterday', label: 'Yesterday' },
    { v: 'this-week', label: 'This Week' }, { v: 'previous-week', label: 'Previous Week' },
    { v: 'this-month', label: 'This Month' }, { v: 'previous-month', label: 'Previous Month' },
    { v: 'this-quarter', label: 'This Quarter' }, { v: 'previous-quarter', label: 'Previous Quarter' },
    { v: 'this-year', label: 'This FY (Apr–Mar)' }, { v: 'previous-year', label: 'Previous FY' },
    { v: 'ytd', label: 'YTD (FY start → today)' }, { v: 'all-time', label: 'All time' },
  ];
  const nowFy = fyOf(new Date());
  const fyList = []; for (let i = 0; i < 6; i++) fyList.push(nowFy - i);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-md border-slate-200 gap-1.5" data-testid="books-daterange-btn">
          <Calendar className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-slate-500">Date Range:</span>
          <span className="font-medium text-slate-800">{range.label}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1">
        <div className="max-h-80 overflow-y-auto">
          {options.map((o) => (
            <button key={o.v} type="button" onClick={() => { setPreset(o.v); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs rounded-md transition ${preset === o.v ? 'bg-violet-600 text-white' : 'hover:bg-slate-100 text-slate-700'}`}>{o.label}</button>
          ))}
          <div className="border-t border-slate-100 mt-1 pt-1">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 px-3 pt-1 pb-0.5">Specific Month</p>
            <div className="max-h-40 overflow-y-auto">
              {monthList.slice(0, 24).map((m) => (
                <button key={m.key} type="button" onClick={() => { setPreset('specific-month'); setSpecificMonth(m.key); setOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs rounded-md transition ${preset === 'specific-month' && specificMonth === m.key ? 'bg-violet-600 text-white' : 'hover:bg-slate-100 text-slate-700'}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-slate-100 mt-1 pt-1">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 px-3 pt-1 pb-0.5">Specific FY</p>
            {fyList.map((yr) => (
              <button key={yr} type="button" onClick={() => { setPreset('specific-fy'); setSpecificFy(yr); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs rounded-md transition ${preset === 'specific-fy' && specificFy === yr ? 'bg-violet-600 text-white' : 'hover:bg-slate-100 text-slate-700'}`}>
                FY {String(yr).slice(-2)}–{String(yr + 1).slice(-2)}
              </button>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-1 pt-1">
            <button type="button" onClick={() => setPreset('custom')}
              className={`w-full text-left px-3 py-2 text-xs rounded-md transition ${preset === 'custom' ? 'bg-violet-600 text-white' : 'hover:bg-slate-100 text-slate-700'}`}>Custom Range…</button>
          </div>
        </div>
        {preset === 'custom' && (
          <div className="border-t border-slate-100 p-2 space-y-2">
            <div><label className="text-[10px] text-slate-500 uppercase">From</label><Input type="date" value={fmt(custom.from)} onChange={(e) => setCustom((p) => ({ ...p, from: new Date(e.target.value) }))} className="h-8 text-xs" /></div>
            <div><label className="text-[10px] text-slate-500 uppercase">To</label><Input type="date" value={fmt(custom.to)} onChange={(e) => setCustom((p) => ({ ...p, to: new Date(e.target.value) }))} className="h-8 text-xs" /></div>
            <Button size="sm" className="w-full h-7 text-xs bg-violet-600 hover:bg-violet-700" onClick={() => setOpen(false)}>Apply</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── Salesperson filter ────────────────────────────────
function SalespersonFilter({ all, state, setState }) {
  const [open, setOpen] = useState(false); const [q, setQ] = useState('');
  const filtered = all.filter((n) => n.toLowerCase().includes(q.toLowerCase()));
  const toggle = (name) => setState((p) => ({ ...p, selected: p.selected.includes(name) ? p.selected.filter((x) => x !== name) : [...p.selected, name] }));
  const summary = state.selected.length === 0 && !['is empty', 'is not empty'].includes(state.op)
    ? 'Salesperson: All'
    : ['is empty', 'is not empty'].includes(state.op) ? `Salesperson ${state.op}` : `Salesperson ${state.op} ${state.selected.length}${state.group ? ' · Grouped' : ''}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-md border-slate-200 gap-1.5" data-testid="books-salesperson-btn">
          <Users className="w-3.5 h-3.5 text-slate-500" />
          <span className="font-medium text-slate-800">{summary}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Select value={state.op} onValueChange={(v) => setState((p) => ({ ...p, op: v }))}>
            <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="is in">is in</SelectItem>
              <SelectItem value="is not in">is not in</SelectItem>
              <SelectItem value="is empty">is empty</SelectItem>
              <SelectItem value="is not empty">is not empty</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
            <Checkbox checked={state.group} onCheckedChange={(v) => setState((p) => ({ ...p, group: !!v }))} />
            <Layers className="w-3 h-3" /> Group as one
          </label>
        </div>
        {(state.op === 'is in' || state.op === 'is not in') && (
          <>
            <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-7 text-xs mb-2" />
            <div className="max-h-52 overflow-y-auto space-y-0.5">
              {filtered.map((name) => (
                <label key={name} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-xs">
                  <Checkbox checked={state.selected.includes(name)} onCheckedChange={() => toggle(name)} />
                  <span className="text-slate-700">{name}</span>
                </label>
              ))}
            </div>
          </>
        )}
        <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100">
          <button className="text-[11px] text-slate-500 hover:text-slate-800" onClick={() => setState({ op: 'is in', selected: [], group: false })}>Clear</button>
          <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700" onClick={() => setOpen(false)}>Done</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Advanced filter builder ────────────────────────────
const FIELD_CATALOG = [
  { group: 'Customer', items: [
    { id: 'customer_name', label: 'Customer Name', type: 'text' },
    { id: 'client_type', label: 'Client Type', type: 'enum', options: ['Retail', 'Corporate'] },
  ]},
  { group: 'Invoice', items: [
    { id: 'invoice_number', label: 'Invoice Number', type: 'text' },
    { id: 'invoice_status', label: 'Invoice Status', type: 'enum', options: ['approved', 'sent', 'partially_paid', 'paid', 'overdue', 'rejected'] },
    { id: 'invoice_date', label: 'Invoice Date', type: 'date' },
    { id: 'due_date', label: 'Due Date', type: 'date' },
    { id: 'reference_number', label: 'Reference Number', type: 'text' },
    { id: 'total', label: 'Total Amount', type: 'number' },
    { id: 'balance', label: 'Outstanding Balance', type: 'number' },
    { id: 'write_off_amount', label: 'Write-off Amount', type: 'number' },
  ]},
  { group: 'People', items: [
    { id: 'salesperson_name', label: 'Salesperson', type: 'enum', options: [] },
  ]},
];
const OPS = {
  text: ['is', 'is not', 'contains', 'does not contain', 'starts with', 'is in', 'is not in', 'is empty', 'is not empty'],
  number: ['=', '≠', '<', '>', '≤', '≥', 'is empty', 'is not empty'],
  date: ['is', 'is before', 'is after', 'is empty', 'is not empty'],
  enum: ['is', 'is not', 'is in', 'is not in', 'is empty', 'is not empty'],
};

function AdvancedFilterBuilder({ rows, setRows, allSalespersons, onApply }) {
  const [open, setOpen] = useState(false);
  const addRow = () => setRows((p) => [...p, { id: Date.now() + Math.random(), field: '', op: '', value: '' }]);
  const updateRow = (id, patch) => setRows((p) => p.map(r => r.id === id ? { ...r, ...patch } : r));
  const removeRow = (id) => setRows((p) => p.filter(r => r.id !== id));
  const fieldMeta = (fid) => { for (const g of FIELD_CATALOG) for (const it of g.items) if (it.id === fid) return it; return null; };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-md border-dashed border-slate-300 gap-1.5" data-testid="books-more-filters-btn">
          <Plus className="w-3.5 h-3.5 text-violet-600" />
          <span className="text-slate-700 font-medium">More Filters</span>
          {rows.length > 0 && <Badge className="bg-violet-100 text-violet-700 border-0 h-4 text-[10px] px-1.5">{rows.length}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[720px] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-900">Advanced Filters</h3>
          <span className="text-[11px] text-slate-400">All conditions combined with AND</span>
        </div>
        {rows.length === 0 && <p className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-md">No filters. Click Add More below.</p>}
        <div className="space-y-2">
          {rows.map((r, idx) => {
            const meta = fieldMeta(r.field);
            const ops = meta ? OPS[meta.type] : [];
            const needsValue = r.op && r.op !== 'is empty' && r.op !== 'is not empty';
            const options = meta?.id === 'salesperson_name' ? allSalespersons : (meta?.options || []);
            const isMulti = r.op === 'is in' || r.op === 'is not in';
            return (
              <div key={r.id} className="grid grid-cols-12 gap-2 items-center bg-slate-50/50 border border-slate-200 rounded-md p-2">
                <span className="col-span-1 text-[10px] font-medium text-slate-500 text-center">{idx + 1}</span>
                <Select value={r.field} onValueChange={(v) => updateRow(r.id, { field: v, op: '', value: '' })}>
                  <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue placeholder="Select field…" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {FIELD_CATALOG.map((g) => (
                      <div key={g.group}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 px-2 pt-2 pb-0.5 font-semibold">{g.group}</div>
                        {g.items.map((it) => <SelectItem key={it.id} value={it.id}>{it.label}</SelectItem>)}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={r.op} onValueChange={(v) => updateRow(r.id, { op: v, value: isMulti ? [] : '' })} disabled={!meta}>
                  <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue placeholder="Select condition…" /></SelectTrigger>
                  <SelectContent>{ops.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}</SelectContent>
                </Select>
                <div className="col-span-4">
                  {!needsValue ? <div className="h-8 text-xs px-2 py-2 text-slate-400">—</div> :
                    meta?.type === 'enum' ? (isMulti ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-full justify-start text-xs font-normal">
                            {Array.isArray(r.value) && r.value.length > 0 ? `${r.value.length} selected` : 'Select values…'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2 max-h-64 overflow-y-auto">
                          {options.map((opt) => {
                            const arr = Array.isArray(r.value) ? r.value : [];
                            const checked = arr.includes(opt);
                            return (
                              <label key={opt} className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-slate-50 px-1 rounded">
                                <Checkbox checked={checked} onCheckedChange={() => updateRow(r.id, { value: checked ? arr.filter(x => x !== opt) : [...arr, opt] })} />
                                <span>{opt}</span>
                              </label>
                            );
                          })}
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Select value={r.value} onValueChange={(v) => updateRow(r.id, { value: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select value…" /></SelectTrigger>
                        <SelectContent>{options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                      </Select>
                    )) : meta?.type === 'date' ? (
                      <Input type="date" value={r.value} onChange={(e) => updateRow(r.id, { value: e.target.value })} className="h-8 text-xs" />
                    ) : meta?.type === 'number' ? (
                      <Input type="number" value={r.value} onChange={(e) => updateRow(r.id, { value: e.target.value })} placeholder="Number" className="h-8 text-xs" />
                    ) : (
                      <Input value={r.value} onChange={(e) => updateRow(r.id, { value: e.target.value })} placeholder="Text" className="h-8 text-xs" />
                    )
                  }
                </div>
                <button onClick={() => removeRow(r.id)} className="col-span-1 flex items-center justify-center text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <button onClick={addRow} className="text-xs text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add More</button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setRows([]); setOpen(false); onApply?.(); }}>Clear All</Button>
            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => { setOpen(false); onApply?.(); }}>Apply</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const fmtInr = (n, showRupee = true) => {
  if (n == null || isNaN(n)) return '—';
  const sign = showRupee ? '₹' : '';
  const rounded = Math.round(n);
  // Indian numbering system: 1,88,60,242 (last 3 digits grouped, then groups of 2)
  const s = String(Math.abs(rounded));
  let lastThree = s.slice(-3);
  const otherNumbers = s.slice(0, -3);
  const formatted = otherNumbers
    ? otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree
    : lastThree;
  return `${n < 0 ? '-' : ''}${sign}${formatted}`;
};
const fmtCurrency = (n, code) => {
  if (n == null || isNaN(n)) return '—';
  const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ', SGD: 'S$' };
  const sym = symbols[code] || (code ? code + ' ' : '₹');
  const rounded = Number(n).toFixed(2);
  // Use en-IN for INR, en-US for others
  const locale = code === 'INR' || !code ? 'en-IN' : 'en-US';
  return `${sym}${Number(rounded).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function BooksAnalyticsPage() {
  const [preset, setPreset] = useState('this-year');
  const [specificFy, setSpecificFy] = useState(fyOf(new Date()));
  const [specificMonth, setSpecificMonth] = useState(() => {
    const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
  });
  const [custom, setCustom] = useState({ from: new Date(new Date().getFullYear(), 3, 1), to: new Date() });
  const [clientType, setClientType] = useState('all');
  const [statusFilter, setStatusFilter] = useState([]);  // multi-select array
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [spState, setSpState] = useState({ op: 'is in', selected: [], group: false });
  const [advRows, setAdvRows] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');

  const [tab, setTab] = useState('overview');
  const [meta, setMeta] = useState({ salespersons: [], statuses: [], client_types: [], last_sync: null });
  const [billCol, setBillCol] = useState({ rows: [], totals: null });
  const [aging, setAging] = useState({ buckets: {}, customers: [], salespersons: [], bucket_labels: [] });
  const [agingGroupBy, setAgingGroupBy] = useState('customer'); // 'customer' | 'salesperson'
  const [customBuckets, setCustomBuckets] = useState('30,60,90'); // days cutoffs
  const [bySp, setBySp] = useState([]);
  const [byCt, setByCt] = useState([]);
  const [collByCt, setCollByCt] = useState([]);
  const [topCust, setTopCust] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSyncMeta, setLastSyncMeta] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null); // customer_id
  const [expandedSalesperson, setExpandedSalesperson] = useState(null); // salesperson_name
  const [expandedClientType, setExpandedClientType] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState({});     // cust_id::bucket -> invoices[]
  const [salespersonInvoices, setSalespersonInvoices] = useState({}); // sp_name::bucket -> invoices[]
  const [clientTypeInvoices, setClientTypeInvoices] = useState({}); // client_type -> invoices[]
  const [agingBucketTab, setAgingBucketTab] = useState('all'); // 'all' or specific label
  const [collectionMode, setCollectionMode] = useState(false);
  const [kpiDrill, setKpiDrill] = useState({ open: false, type: null, title: '', subtitle: '', invoices: [], total: 0, loading: false });
  const [kpiDrillGroup, setKpiDrillGroup] = useState('none'); // none | category | salesperson | client_type
  const [kpiDrillGroup2, setKpiDrillGroup2] = useState('none');
  const [pmtDrillGroup, setPmtDrillGroup] = useState('none');
  const [pmtDrillGroup2, setPmtDrillGroup2] = useState('none');
  const [excludeRejected, setExcludeRejected] = useState(true);
  const [kpiDrillExpanded, setKpiDrillExpanded] = useState({}); // group key -> bool
  const [pmtDrillExpanded, setPmtDrillExpanded] = useState({});
  // Payments Received tab state
  const [pmtData, setPmtData] = useState(null);
  const [pmtList, setPmtList] = useState({ total: 0, payments: [] });
  const [pmtModes, setPmtModes] = useState([]);
  const [pmtMode, setPmtMode] = useState('all');
  const [pmtLoading, setPmtLoading] = useState(false);
  const [pmtSyncStatus, setPmtSyncStatus] = useState(null); // { last_sync_at, total_payments }
  const [pmtSubTab, setPmtSubTab] = useState('mode'); // mode | salesperson | customer | list
  const [voidState, setVoidState] = useState({ open: false, count: 0, total_voided_value: 0, invoices: [] });
  const [aboutOpen, setAboutOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [pmtDrill, setPmtDrill] = useState({ open: false, title: '', subtitle: '', payments: [], loading: false });
  const [nightly, setNightly] = useState(null);
  const [nightlyOpen, setNightlyOpen] = useState(false);
  const [promiseOverdueCount, setPromiseOverdueCount] = useState(0);
  const [slipTrackerOpen, setSlipTrackerOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [paymentsTrendOpen, setPaymentsTrendOpen] = useState(false);
  const [voidPaymentsOpen, setVoidPaymentsOpen] = useState(false);

  const fetchPromiseOverdue = useCallback(async () => {
    try { const r = await api.get('/books/invoices', { params: { expected_overdue: 1, limit: 1 } }); setPromiseOverdueCount(r.data.total || 0); } catch { }
  }, []);
  useEffect(() => { fetchPromiseOverdue(); }, [fetchPromiseOverdue]);

  const fetchNightly = useCallback(async () => {
    try { const r = await api.get('/books/nightly-sync'); setNightly(r.data); } catch { }
  }, []);
  useEffect(() => { fetchNightly(); }, [fetchNightly]);

  const saveNightly = async (patch) => {
    try {
      const cur = { enabled: nightly.enabled, time: `${String(nightly.hour_ist).padStart(2, '0')}:${String(nightly.minute_ist).padStart(2, '0')}` };
      const r = await api.put('/books/nightly-sync', { ...cur, ...patch });
      setNightly((n) => ({ ...n, ...r.data }));
      toast.success(`Nightly sync ${r.data.enabled ? `on at ${String(r.data.hour_ist).padStart(2, '0')}:${String(r.data.minute_ist).padStart(2, '0')} IST` : 'turned off'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update nightly sync'); }
  };

  const runNightlyNow = async () => {
    try { const r = await api.post('/books/nightly-sync/run-now'); toast.info(r.data.message); setTimeout(fetchNightly, 8000); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to start'); }
  };


  // Sync freshness traffic light (green <15m, amber 15-60m, red >60m)
  const syncFreshness = useMemo(() => {
    if (!meta.last_sync) return { color: 'bg-slate-300', label: 'No sync yet', mins: null };
    const mins = Math.floor((Date.now() - new Date(meta.last_sync).getTime()) / 60000);
    if (mins < 15) return { color: 'bg-emerald-500', label: `Fresh · ${mins}m ago`, mins };
    if (mins < 60) return { color: 'bg-amber-400', label: `${mins}m ago`, mins };
    if (mins < 1440) return { color: 'bg-red-500', label: `${Math.floor(mins / 60)}h ago`, mins };
    return { color: 'bg-red-600', label: `${Math.floor(mins / 1440)}d ago`, mins };
  }, [meta.last_sync]);

  const zohoOrgId = meta.zoho_org_id || '';
  const zohoInvoiceUrl = (invoiceId) => zohoOrgId && invoiceId ? `https://books.zoho.com/app/${zohoOrgId}/#/invoices/${invoiceId}` : null;

  const range = useMemo(
    () => (preset === 'custom' ? { ...custom, label: 'Custom' } : computeRange(preset, preset === 'specific-month' ? specificMonth : specificFy)),
    [preset, specificFy, specificMonth, custom]
  );
  const rangeFromISO = fmt(range.from);
  const rangeToISO = fmt(range.to);

  // Build query params
  const buildParams = useCallback(() => {
    const p = { from_date: rangeFromISO, to_date: rangeToISO };
    if (clientType !== 'all') p.client_type = clientType;
    if (statusFilter.length > 0) p.status = JSON.stringify(statusFilter);
    if (spState.op) p.salesperson_op = spState.op;
    if (spState.selected.length > 0) p.salesperson_names = JSON.stringify(spState.selected);
    if (customerSearch) p.customer_search = customerSearch;
    if (!excludeRejected) p.include_rejected = '1';
    if (advRows.length > 0) {
      const clean = advRows.filter(r => r.field && r.op);
      if (clean.length > 0) p.advanced_filters = JSON.stringify(clean.map(r => ({ field: r.field, op: r.op, value: r.value })));
    }
    return p;
  }, [rangeFromISO, rangeToISO, clientType, statusFilter, spState, customerSearch, advRows, excludeRejected]);

  // Fetch invoices for a specific customer / salesperson drilldown.
  // Bucket-aware: when a specific AR aging tab is active, pass min/max aging
  // days so drilldown only shows invoices contributing to that bucket.
  const fetchCustomerInvoices = useCallback(async (customerId, customerName, bucketLabel) => {
    const isBucket = bucketLabel && bucketLabel !== 'all';
    const cacheKey = isBucket ? `${customerId}::${bucketLabel}` : `${customerId}::all`;
    if (customerInvoices[cacheKey]) return;
    try {
      const params = { limit: 500, customer_search: customerName };
      if (isBucket) {
        const { min, max } = parseBucket(bucketLabel);
        if (min != null) params.min_aging_days = min;
        if (max != null) params.max_aging_days = max;
      } else {
        // AR aging "All" tab → only invoices with outstanding
        params.min_balance = 0;
      }
      const r = await api.get('/books/invoices', { params });
      setCustomerInvoices((p) => ({ ...p, [cacheKey]: r.data.rows || [] }));
    } catch (err) {
      toast.error('Failed to load invoices');
    }
  }, [customerInvoices]);

  const fetchSalespersonInvoices = useCallback(async (spName, bucketLabel) => {
    const isBucket = bucketLabel && bucketLabel !== 'all';
    const cacheKey = isBucket ? `${spName}::${bucketLabel}` : `${spName}::all`;
    if (salespersonInvoices[cacheKey]) return;
    try {
      const params = { limit: 500, salesperson_op: 'is in', salesperson_names: JSON.stringify([spName]) };
      if (isBucket) {
        const { min, max } = parseBucket(bucketLabel);
        if (min != null) params.min_aging_days = min;
        if (max != null) params.max_aging_days = max;
      } else {
        params.min_balance = 0;
      }
      const r = await api.get('/books/invoices', { params });
      setSalespersonInvoices((p) => ({ ...p, [cacheKey]: r.data.rows || [] }));
    } catch (err) {
      toast.error('Failed to load invoices');
    }
  }, [salespersonInvoices]);

  const fetchClientTypeInvoices = useCallback(async (ct) => {
    if (clientTypeInvoices[ct]) return;
    try {
      const base = buildParams();
      const params = { ...base, limit: 500, client_type: ct };
      const r = await api.get('/books/invoices', { params });
      setClientTypeInvoices((p) => ({ ...p, [ct]: r.data.rows || [] }));
    } catch (err) {
      toast.error('Failed to load invoices');
    }
  }, [clientTypeInvoices, buildParams]);

  // ─── Payments drilldown (Payments Received tab rows) ─────
  const openPmtDrill = async ({ title, subtitle, extraParams = {} }) => {
    setPmtDrillGroup('none');
    setPmtDrillGroup2('none');
    setPmtDrillExpanded({});
    setPmtDrill({ open: true, title, subtitle, payments: [], loading: true });
    try {
      const params = {
        from: rangeFromISO, to: rangeToISO,
        limit: 500,
        ...(pmtMode !== 'all' ? { mode: pmtMode } : {}),
        ...(customerSearch ? { customer_search: customerSearch } : {}),
        ...extraParams,
      };
      const r = await api.get('/books/payments/list', { params });
      setPmtDrill((prev) => ({ ...prev, payments: r.data.payments || [], loading: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payments');
      setPmtDrill((prev) => ({ ...prev, loading: false }));
    }
  };

  // ─── Generic invoice drilldown (used across chart/tabs) ────
  const openInvoiceDrill = async ({ title, subtitle, extraParams = {} }) => {
    const base = buildParams();
    const params = { ...base, ...extraParams, limit: 500 };
    setKpiDrillGroup('none');
    setKpiDrillGroup2('none');
    setKpiDrill({ open: true, type: null, title, subtitle, invoices: [], total: 0, loading: true });
    try {
      const r = await api.get('/books/invoices', { params });
      setKpiDrill((prev) => ({ ...prev, invoices: r.data.rows || [], total: r.data.total || 0, loading: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load drilldown');
      setKpiDrill((prev) => ({ ...prev, loading: false }));
    }
  };

  // ─── Broken promises: balance open but expected payment date passed ───
  const openExpectedOverdueDrill = async () => {
    setKpiDrillGroup('none');
    setKpiDrillGroup2('none');
    const today = new Date().toISOString().slice(0, 10);
    setKpiDrill({ open: true, type: null, title: 'Expected Date Passed — Payment Not Received', subtitle: `Open-balance invoices whose expected payment date is before ${today} · all time, ignores page filters`, invoices: [], total: 0, loading: true });
    try {
      const r = await api.get('/books/invoices', { params: { expected_overdue: 1, limit: 500 } });
      setKpiDrill((prev) => ({ ...prev, invoices: r.data.rows || [], total: r.data.total || 0, loading: false }));
      setPromiseOverdueCount(r.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
      setKpiDrill((prev) => ({ ...prev, loading: false }));
    }
  };

  // ─── Billing vs Collections table drilldowns ────────────
  const monthBoundsOf = (moKey) => {
    const [yy, mm] = String(moKey).split('-').map(Number);
    return { from: fmt(new Date(yy, mm - 1, 1)), to: fmt(new Date(yy, mm, 0)) };
  };

  const drillMonthInvoices = (moKey, pendingOnly = false) => {
    const { from, to } = monthBoundsOf(moKey);
    openInvoiceDrill({
      title: `${pendingOnly ? 'Pending Collection' : 'Billing'} — ${moKey}`,
      subtitle: `${from} → ${to} · ${pendingOnly ? 'invoices with balance still open' : 'all invoices raised in this month'}`,
      extraParams: pendingOnly ? { from_date: from, to_date: to, min_balance: 0 } : { from_date: from, to_date: to },
    });
  };

  const drillMonthPayments = (moKey, label) => {
    const { from, to } = monthBoundsOf(moKey);
    openPmtDrill({ title: `${label} — ${moKey}`, subtitle: `${from} → ${to} · payments received in this month`, extraParams: { from, to } });
  };

  // ─── One-click Collection Mode ─────────────────────────
  // Focus on receivables aged > 45 days.
  const enableCollectionMode = () => {
    setTab('aging');
    setAgingGroupBy('customer');
    setCustomBuckets('45');
    setAgingBucketTab('46+');
    setCollectionMode(true);
    // Clear any customer expansion since bucket has changed
    setExpandedCustomer(null);
    setExpandedSalesperson(null);
    setCustomerInvoices({});
    setSalespersonInvoices({});
    toast.success('Collection Mode: showing overdue receivables aged > 45 days, sorted by outstanding');
  };

  const clearCollectionMode = () => {
    setCollectionMode(false);
    setCustomBuckets('30,60,90');
    setAgingBucketTab('all');
    setExpandedCustomer(null);
    setExpandedSalesperson(null);
    setCustomerInvoices({});
    setSalespersonInvoices({});
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = buildParams();
    try {
      const [bc, a, sp, ct, cct, tc] = await Promise.all([
        api.get('/books/billing-collections', { params }),
        api.get('/books/aging', { params: { ...params, group: spState.group ? 'true' : 'false', custom_buckets: customBuckets } }),
        api.get('/books/by-salesperson', { params: { ...params, group: spState.group ? 'true' : 'false' } }),
        api.get('/books/by-client-type', { params }),
        api.get('/books/collections-by-client-type', { params }),
        api.get('/books/top-customers', { params: { ...params, limit: 20 } }),
      ]);
      setBillCol(bc.data);
      setAging(a.data);
      setBySp(sp.data.rows || []);
      setByCt(ct.data.rows || []);
      setCollByCt(cct.data.rows || []);
      setTopCust(tc.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [buildParams, spState.group, customBuckets]);

  useEffect(() => {
    api.get('/books/metadata').then((r) => setMeta(r.data)).catch(() => toast.error('Failed to load metadata'));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Void invoice count in current date window (excluded from all reports)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/books/revenue-report/void-invoices', { params: { from: rangeFromISO, to: rangeToISO, customer_search: customerSearch || undefined } });
        setVoidState((s) => ({ ...s, count: r.data.count, total_voided_value: r.data.total_voided_value, invoices: r.data.invoices }));
      } catch { /* ignore */ }
    })();
  }, [rangeFromISO, rangeToISO, customerSearch]);

  // ─── Payments Received tab ───
  const fetchPmtStatus = useCallback(async () => {
    try {
      const r = await api.get('/books/payments/sync/status');
      setPmtSyncStatus(r.data);
    } catch { /* ignore */ }
  }, []);

  const fetchPmtModes = useCallback(async () => {
    try {
      const r = await api.get('/books/payments/modes');
      setPmtModes(r.data.modes || []);
    } catch { /* ignore */ }
  }, []);

  const fetchPmtData = useCallback(async () => {
    if (tab !== 'payments') return;
    setPmtLoading(true);
    try {
      const params = { from: rangeFromISO, to: rangeToISO };
      if (pmtMode !== 'all') params.mode = pmtMode;
      if (customerSearch) params.customer_search = customerSearch;
      // Propagate parent filters so the tab respects the same slice as the rest of the dashboard
      if (spState.op && spState.selected.length > 0) {
        params.salesperson_op = spState.op;
        params.salesperson_names = JSON.stringify(spState.selected);
      }
      if (clientType && clientType !== 'all') params.client_type = clientType;
      const [agg, list] = await Promise.all([
        api.get('/books/payments', { params }),
        api.get('/books/payments/list', { params: { ...params, limit: 200 } }),
      ]);
      setPmtData(agg.data);
      setPmtList(list.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payments');
    } finally {
      setPmtLoading(false);
    }
  }, [tab, rangeFromISO, rangeToISO, pmtMode, customerSearch, spState.op, spState.selected, clientType]);

  useEffect(() => {
    if (tab === 'payments') {
      fetchPmtData();
      if (!pmtSyncStatus) fetchPmtStatus();
      if (pmtModes.length === 0) fetchPmtModes();
    }
  }, [tab, fetchPmtData, fetchPmtStatus, fetchPmtModes, pmtSyncStatus, pmtModes.length]);

  // Payments now sync via the header "Sync from Zoho" button + nightly job.

  // Combined refresh so header buttons (Run Report / Refresh) update the Payments tab too
  const fetchAllAndPmt = useCallback(async () => {
    await fetchAll();
    if (tab === 'payments') await fetchPmtData();
  }, [fetchAll, fetchPmtData, tab]);

  // When the AR aging bucket tab changes, clear open expansions & cached drilldowns
  // so re-opening fetches invoices scoped to the new bucket.
  useEffect(() => {
    setExpandedCustomer(null);
    setExpandedSalesperson(null);
  }, [agingBucketTab]);

  // ─── Drilldown grouping (client-side, two levels) ───────
  const kpiKeyOf = (inv, dim) => dim === 'category' ? (inv.cf_branch_name || 'Not mentioned')
    : dim === 'salesperson' ? (inv.salesperson_name || '— Unassigned —')
    : (inv.cf_client_type || 'Not set');

  const kpiDrillGroups = useMemo(() => {
    if (kpiDrillGroup === 'none') return null;
    const g2 = kpiDrillGroup2 !== 'none' && kpiDrillGroup2 !== kpiDrillGroup ? kpiDrillGroup2 : null;
    const m = new Map();
    for (const inv of kpiDrill.invoices) {
      const k1 = kpiKeyOf(inv, kpiDrillGroup);
      if (!m.has(k1)) m.set(k1, { key: k1, invoices: [], total: 0, balance: 0, subs: new Map() });
      const g = m.get(k1);
      const t = Number(inv.bcy_total ?? inv.total) || 0, b = Number(inv.bcy_balance ?? inv.balance) || 0;
      g.invoices.push(inv); g.total += t; g.balance += b;
      if (g2) {
        const k2 = kpiKeyOf(inv, g2);
        if (!g.subs.has(k2)) g.subs.set(k2, { key: k2, invoices: [], total: 0, balance: 0 });
        const s2 = g.subs.get(k2);
        s2.invoices.push(inv); s2.total += t; s2.balance += b;
      }
    }
    return [...m.values()].map((g) => ({ ...g, subs: [...g.subs.values()].sort((a, b) => b.total - a.total) })).sort((a, b) => b.total - a.total);
  }, [kpiDrill.invoices, kpiDrillGroup, kpiDrillGroup2]);

  const pmtFacetsOf = (p, dim, amt) => {
    if (dim === 'category') {
      const cats = (p.categories || []).filter((c) => c.amount_bcy);
      return cats.length ? cats.map((c) => [c.category || 'Not mentioned', c.amount_bcy]) : [['Not mentioned', amt]];
    }
    if (dim === 'salesperson') return [[p.salesperson_name || '— Unassigned —', amt]];
    return [[p.client_type || '(Unassigned)', amt]];
  };

  const pmtDrillGroups = useMemo(() => {
    if (pmtDrillGroup === 'none') return null;
    const g2dim = pmtDrillGroup2 !== 'none' && pmtDrillGroup2 !== pmtDrillGroup ? pmtDrillGroup2 : null;
    const m = new Map();
    for (const p of pmtDrill.payments) {
      const full = p.amount_bcy ?? ((p.amount || 0) * (p.exchange_rate || 1));
      for (const [k1, a1] of pmtFacetsOf(p, pmtDrillGroup, full)) {
        if (!m.has(k1)) m.set(k1, { key: k1, payments: [], amount: 0, subs: new Map() });
        const g = m.get(k1);
        g.payments.push({ ...p, _portion: a1 });
        g.amount += Number(a1) || 0;
        if (g2dim) {
          for (const [k2, a2] of pmtFacetsOf(p, g2dim, a1)) {
            if (!g.subs.has(k2)) g.subs.set(k2, { key: k2, payments: [], amount: 0 });
            const s2 = g.subs.get(k2);
            s2.payments.push({ ...p, _portion: a2 });
            s2.amount += Number(a2) || 0;
          }
        }
      }
    }
    return [...m.values()].map((g) => ({ ...g, subs: [...g.subs.values()].sort((a, b) => b.amount - a.amount) })).sort((a, b) => b.amount - a.amount);
  }, [pmtDrill.payments, pmtDrillGroup, pmtDrillGroup2]);

  // ─── Collection by Client Type (cash-in ⊕ collected-on-billing) ───
  const collCtRows = useMemo(() => {
    const m = new Map();
    byCt.forEach((r) => m.set(r.client_type, { client_type: r.client_type, collected: r.collected, cash_in: 0, payments: 0 }));
    (collByCt || []).forEach((r) => {
      const e = m.get(r.client_type) || { client_type: r.client_type, collected: 0, cash_in: 0, payments: 0 };
      e.cash_in = r.cash_in; e.payments = r.payments;
      m.set(r.client_type, e);
    });
    const rows = [...m.values()].sort((a, b) => b.cash_in - a.cash_in);
    const maxCash = Math.max(...rows.map((r) => r.cash_in), 1);
    return rows.map((r) => ({ ...r, pct: Math.round((r.cash_in / maxCash) * 100) }));
  }, [byCt, collByCt]);

  const kpiDrillTotals = useMemo(() => {
    let total = 0, balance = 0;
    for (const inv of kpiDrill.invoices) {
      total += Number(inv.bcy_total ?? inv.total) || 0;
      balance += Number(inv.bcy_balance ?? inv.balance) || 0;
    }
    return { total, balance };
  }, [kpiDrill.invoices]);

  const pmtDrillTotal = useMemo(
    () => pmtDrill.payments.reduce((s, p) => s + (Number(p.amount_bcy ?? ((p.amount || 0) * (p.exchange_rate || 1))) || 0), 0),
    [pmtDrill.payments]
  );

  const renderKpiInvRow = (inv, keySuffix = '') => {
    const url = zohoInvoiceUrl(inv.invoice_id);
    const expDate = inv.expected_payment_date || inv.payment_expected_date || '';
    const expPassed = expDate && expDate < new Date().toISOString().slice(0, 10) && (inv.bcy_balance ?? inv.balance) > 0;
    return (
      <div key={inv.invoice_id + keySuffix} className="grid grid-cols-11 gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-violet-50">
        <div className="col-span-2 font-medium text-slate-700 whitespace-nowrap">{inv.invoice_number || '—'}</div>
        <div className="col-span-2 text-slate-700 truncate">{inv.customer_name}</div>
        <div className="text-slate-500">{inv.date}</div>
        <div className={expPassed ? 'text-rose-600 font-semibold' : expDate ? 'text-sky-700 font-medium' : 'text-slate-300'} data-testid="kpi-inv-expected-date" title={expPassed ? 'Expected payment date has passed with balance still open' : ''}>{expDate || '—'}</div>
        <div className="text-right text-slate-700">{fmtCurrency(inv.total, inv.currency_code)}</div>
        <div className={`text-right font-medium ${inv.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtCurrency(inv.balance, inv.currency_code)}</div>
        <div><Badge className="bg-slate-100 text-slate-700 text-[9px] py-0">{inv.status}</Badge></div>
        <div className="text-slate-500 truncate">{inv.salesperson_name || '—'}</div>
        <div>{url && <a href={url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline text-[10px] font-medium">Open ↗</a>}</div>
      </div>
    );
  };

  const renderPmtDrillRow = (p, key) => {
    const full = p.amount_bcy ?? ((p.amount || 0) * (p.exchange_rate || 1));
    const shown = p._portion != null ? p._portion : full;
    const partial = p._portion != null && Math.round(p._portion) !== Math.round(full);
    return (
      <div key={key} className="grid grid-cols-12 gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-emerald-50">
        <div className="text-slate-500">{p.date}</div>
        <div className="font-medium text-slate-700 whitespace-nowrap">{p.payment_number || '—'}</div>
        <div className="col-span-3 text-slate-800 truncate" title={p.customer_name}>{p.customer_name}</div>
        <div className="col-span-2 text-slate-600 truncate" title={p.payment_mode}>{p.payment_mode}</div>
        <div className="text-slate-500 truncate" title={p.reference_number}>{p.reference_number || '—'}</div>
        <div className="col-span-2 text-slate-600 truncate">
          {p.invoices?.slice(0, 2).map((inv, i) => (
            <a key={inv.invoice_id + '-' + i} href={`https://books.zoho.com/app/699857543/#/invoices/${inv.invoice_id}`} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline mr-1 whitespace-nowrap">{inv.invoice_number}↗</a>
          ))}
          {p.invoices?.length > 2 && <span className="text-slate-400">+{p.invoices.length - 2}</span>}
        </div>
        <div className="text-slate-500 truncate" title={p.salesperson_name}>{p.salesperson_name || '—'}</div>
        <div className="text-right tabular-nums font-semibold text-emerald-700">
          ₹{Math.round(shown).toLocaleString('en-IN')}
          {partial && <div className="text-[9px] text-slate-400 font-normal">of ₹{Math.round(full).toLocaleString('en-IN')}</div>}
          {!partial && p.currency_code && p.currency_code !== 'INR' && (
            <div className="text-[9px] text-slate-400 font-normal">{p.currency_code} {Math.round(p.amount).toLocaleString('en-IN')}</div>
          )}
        </div>
      </div>
    );
  };

  // Shared column-definitions footnote for the Overview-style tabs
  const columnsFootnote = (rowNoun) => (
    <p className="text-[10px] text-slate-500 mt-3 leading-relaxed" data-testid={`columns-footnote-${rowNoun.replace(/\s/g, '-')}`}>
      <b className="text-violet-700">Billing</b> = invoices raised in the selected period for this {rowNoun} &nbsp;·&nbsp;
      <b className="text-emerald-700">Collections</b> = cash received in the selected period from this {rowNoun} &nbsp;·&nbsp;
      <b className="text-cyan-700">Current Billing Collection</b> = of Collections, portion paid against invoices raised in the same month as the payment &nbsp;·&nbsp;
      <b className="text-slate-600">Previous Invoice Collection</b> = of Collections, portion paid against older invoices &nbsp;·&nbsp;
      <b className="text-amber-700">Pending Collection</b> = still-open portion of this {rowNoun}&apos;s Billing &nbsp;·&nbsp;
      <b>Coll %</b> = (Billing − Pending) ÷ Billing
    </p>
  );

  const buildExportData = () => {
    let rows = [], headers = [];
    if (tab === 'salesperson') {      headers = ['Salesperson', 'Billing', 'Collections', 'Current Billing Collection', 'Previous Invoice Collection', 'Pending Collection', 'Coll %', 'Invoices'];
      rows = bySp.map(r => [r.salesperson_name, r.invoiced, r.collections ?? 0, r.current_collection ?? 0, r.previous_collection ?? 0, r.outstanding, r.coll_pct ?? 0, r.invoices]);
    } else if (tab === 'aging') {
      if (agingGroupBy === 'customer') {
        headers = ['Customer', ...(aging.bucket_labels || []).map(l => `${l}d`), 'Total Due', 'Salesperson'];
        rows = aging.customers.map(c => [c.customer_name, ...(aging.bucket_labels || []).map(l => c.buckets?.[l] || 0), c.total_due, c.salesperson_name]);
      } else {
        headers = ['Salesperson', ...(aging.bucket_labels || []).map(l => `${l}d`), 'Total Due', 'Customers', 'Invoices'];
        rows = aging.salespersons.map(s => [s.salesperson_name, ...(aging.bucket_labels || []).map(l => s.buckets?.[l] || 0), s.total_due, s.customer_count, s.count]);
      }
    } else if (tab === 'topcustomers') {
      headers = ['Customer', 'Billing', 'Collections', 'Current Billing Collection', 'Previous Invoice Collection', 'Pending Collection', 'Coll %', 'Invoices'];
      rows = topCust.map(c => [c.customer_name, c.invoiced, c.collections ?? 0, c.current_collection ?? 0, c.previous_collection ?? 0, c.outstanding, c.coll_pct ?? 0, c.invoices]);
    } else if (tab === 'clienttype') {
      headers = ['Client Type', 'Billing', 'Collections', 'Current Billing Collection', 'Previous Invoice Collection', 'Pending Collection', 'Coll %', 'Invoices'];
      rows = byCt.map(r => [r.client_type, r.invoiced, r.collections ?? 0, r.current_collection ?? 0, r.previous_collection ?? 0, r.outstanding, r.coll_pct ?? 0, r.invoices]);
    } else if (tab === 'payments') {
      // Export the currently-active payment sub-tab
      const data = pmtData?.[`by_${pmtSubTab}`] || [];
      if (pmtSubTab === 'list') {
        headers = ['Date', 'Payment #', 'Customer', 'Mode', 'Reference', 'Salesperson', 'Currency', 'Amount', 'Amount (INR)'];
        rows = (pmtList.payments || []).map(p => [p.date, p.payment_number, p.customer_name, p.payment_mode, p.reference_number, p.salesperson_name || '', p.currency_code, p.amount, p.amount_bcy ?? p.amount * (p.exchange_rate || 1)]);
      } else {
        headers = [pmtSubTab === 'mode' ? 'Mode' : pmtSubTab === 'category' ? 'Category' : pmtSubTab === 'salesperson' ? 'Salesperson' : 'Customer', 'Payments', 'Amount (INR)'];
        rows = data.map(r => [r.group, r.count, r.amount]);
      }
    } else {
      headers = ['Month', 'Billing', 'Collections', 'Current Billing Collection', 'Previous Invoice Collection', 'Pending Collection', 'Coll %', 'Invoices'];
      rows = billCol.rows.map(r => [r.month, r.billing, r.collections, r.current_collection, r.previous_collection, r.pending, r.coll_pct, r.inv_count]);
      if (billCol.totals) rows.push(['GRAND TOTAL', billCol.totals.billing, billCol.totals.collections, billCol.totals.current_collection, billCol.totals.previous_collection, billCol.totals.pending, billCol.totals.coll_pct, billCol.totals.inv_count]);
    }
    return { headers, rows };
  };

  const filename = (ext) => `books-${tab}${tab === 'payments' ? '-' + pmtSubTab : ''}-${fmt(new Date())}.${ext}`;

  const filterSummary = () => {
    const bits = [`Range: ${fmt(range.from)} → ${fmt(range.to)} (${range.label})`];
    if (customerSearch) bits.push(`Customer: ${customerSearch}`);
    if (clientType !== 'all') bits.push(`Client Type: ${clientType}`);
    if (status !== 'all') bits.push(`Status: ${status}`);
    if (spState.selected.length > 0) bits.push(`Salesperson (${spState.op}): ${spState.selected.slice(0, 3).join(', ')}${spState.selected.length > 3 ? '…' : ''}`);
    return bits.join(' · ');
  };

  const exportCsv = () => {
    const { headers, rows } = buildExportData();
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename('csv'); a.click(); URL.revokeObjectURL(url);
  };

  const exportXlsx = async () => {
    const XLSX = await import('xlsx');
    const { headers, rows } = buildExportData();
    const ws = XLSX.utils.aoa_to_sheet([['Books Analytics'], [filterSummary()], [], headers, ...rows]);
    ws['!cols'] = headers.map((_, i) => ({ wch: i === 0 ? 28 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab.slice(0, 30));
    XLSX.writeFile(wb, filename('xlsx'));
  };

  const exportPdf = async () => {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    const { headers, rows } = buildExportData();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFontSize(14); doc.setTextColor(30, 30, 60);
    doc.text('Books Analytics', 40, 40);
    doc.setFontSize(9); doc.setTextColor(90, 90, 110);
    doc.text(`Tab: ${tab}${tab === 'payments' ? ' · ' + pmtSubTab : ''}   ·   ${filterSummary()}`, 40, 58);
    doc.text(`Generated ${new Date().toLocaleString('en-IN')}`, 40, 72);
    autoTable(doc, {
      startY: 90,
      head: [headers],
      body: rows,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [124, 58, 237], textColor: 255 },
      columnStyles: headers.reduce((a, _, i) => { if (i > 0) a[i] = { halign: 'right' }; return a; }, {}),
    });
    doc.save(filename('pdf'));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1400px] mx-auto px-6 py-6 space-y-5" data-testid="books-analytics-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-violet-600" />
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Books Analytics</h1>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-slate-500">Indian FY (Apr–Mar) · powered by Zoho Books sync</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <GlobalSyncPill />
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-md gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => setSlipTrackerOpen(true)}
              data-testid="books-slip-tracker"
              title="Track how the Expected Payment Date has drifted from its Original value"
            >
              <Calendar className="w-3.5 h-3.5" /> Slip Tracker
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-md gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => setPaymentsOpen(true)}
              data-testid="books-payments-received"
              title="Payments received in a chosen window (Today / Yesterday / Last N days)"
            >
              <Calendar className="w-3.5 h-3.5" /> Payments Received
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-md gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={() => setPaymentsTrendOpen(true)}
              data-testid="books-payments-trend"
              title="Daily trend chart of payments received"
            >
              <Calendar className="w-3.5 h-3.5" /> Payment Trend
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-md gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => setVoidPaymentsOpen(true)}
              data-testid="books-void-payments"
              title="See which payments are excluded from Collections because they were voided in Zoho"
            >
              <Ban className="w-3.5 h-3.5" /> Void Payments
            </Button>
            <span className="relative inline-block">
              <button type="button" onClick={() => setNightlyOpen(!nightlyOpen)} data-testid="nightly-sync-note"
                className={`text-[10px] px-2 py-1 rounded-md border whitespace-nowrap transition ${nightly?.enabled ? 'text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100' : 'text-slate-500 border-slate-200 bg-slate-50 hover:bg-slate-100'}`}>
                ⏾ nightly sync {nightly ? (nightly.enabled ? `on · ${String(nightly.hour_ist).padStart(2, '0')}:${String(nightly.minute_ist).padStart(2, '0')} IST` : 'off') : '…'} ▾
              </button>
              {nightlyOpen && nightly && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNightlyOpen(false)} />
                  <div className="absolute right-0 z-50 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3 space-y-2 text-left" data-testid="nightly-sync-popover">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-700">Nightly auto-sync (invoices + payments + CRM leads)</span>
                      <button type="button" onClick={() => saveNightly({ enabled: !nightly.enabled })} data-testid="nightly-sync-toggle"
                        className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${nightly.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                        {nightly.enabled ? 'ON' : 'OFF'}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-500">Run daily at</span>
                      <input type="time" defaultValue={`${String(nightly.hour_ist).padStart(2, '0')}:${String(nightly.minute_ist).padStart(2, '0')}`}
                        onBlur={(e) => e.target.value && saveNightly({ time: e.target.value })}
                        className="text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5" data-testid="nightly-sync-time" />
                      <span className="text-[11px] text-slate-400">IST</span>
                    </div>
                    {nightly.last_run && (
                      <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-1.5">
                        Last run: <span className={nightly.last_run.status === 'success' ? 'text-emerald-600 font-medium' : 'text-rose-600 font-medium'}>{nightly.last_run.status}</span> · {nightly.last_run.completed_at ? new Date(nightly.last_run.completed_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                        {nightly.last_run.steps?.slice(-1).map((s, i) => <div key={i} className="text-[10px] text-slate-400 truncate">{s}</div>)}
                      </div>
                    )}
                    <button type="button" onClick={runNightlyNow} className="w-full text-[11px] border border-slate-200 rounded-md py-1 hover:bg-slate-50" data-testid="nightly-sync-run-now">▶ Run now</button>
                  </div>
                </>
              )}
            </span>
            <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={fetchAllAndPmt} disabled={loading} data-testid="books-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => setAboutOpen(true)} data-testid="books-about">
              <Info className="w-3.5 h-3.5 mr-1.5" /> About
            </Button>
            <div className="relative">
              <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => setExportOpen((o) => !o)} data-testid="books-export">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {exportOpen && (
                <div className="absolute right-0 top-9 z-30 w-40 rounded-md border border-slate-200 bg-white shadow-lg text-xs" data-testid="books-export-menu">
                  <button onClick={() => { exportCsv(); setExportOpen(false); }} data-testid="books-export-csv" className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 text-left"><FileText className="w-3.5 h-3.5" /> CSV</button>
                  <button onClick={() => { exportXlsx(); setExportOpen(false); }} data-testid="books-export-xlsx" className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 text-left border-t border-slate-100"><FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" /> Excel (.xlsx)</button>
                  <button onClick={() => { exportPdf(); setExportOpen(false); }} data-testid="books-export-pdf" className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 text-left border-t border-slate-100"><FileText className="w-3.5 h-3.5 text-rose-700" /> PDF</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <Card className="p-3 border border-border bg-white flex items-center flex-wrap gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs text-slate-500 font-medium mr-1">Filters:</span>
          <span className="flex items-center gap-1" data-testid="books-range-chips">
            {[['this-month', 'This month'], ['previous-month', 'Last month'], ['this-year', 'This FY'], ['previous-year', 'Last FY'], ['ytd', 'YTD'], ['all-time', 'All time']].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setPreset(v)} data-testid={`books-range-chip-${v}`}
                className={`text-[11px] px-2.5 py-1.5 rounded-md border transition whitespace-nowrap ${preset === v ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
            ))}
          </span>
          <DateRangePicker preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} specificFy={specificFy} setSpecificFy={setSpecificFy} specificMonth={specificMonth} setSpecificMonth={setSpecificMonth} />
          <SalespersonFilter all={meta.salespersons} state={spState} setState={setSpState} />
          <Select value={clientType} onValueChange={setClientType}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="books-clienttype"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Client Types</SelectItem>
              {meta.client_types.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Popover open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs rounded-md border-slate-200 gap-1.5" data-testid="books-status">
                <span className="text-slate-500">Status:</span>
                <span className="font-medium text-slate-800">{statusFilter.length === 0 ? 'All' : `${statusFilter.length} selected`}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 p-2">
              <div className="max-h-64 overflow-y-auto space-y-0.5">
                {meta.statuses.map((s) => {
                  const checked = statusFilter.includes(s);
                  return (
                    <label key={s} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-xs">
                      <Checkbox checked={checked} onCheckedChange={() => setStatusFilter((p) => checked ? p.filter(x => x !== s) : [...p, s])} data-testid={`status-opt-${s}`} />
                      <span className="text-slate-700">{s}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100">
                <button className="text-[11px] text-slate-500 hover:text-slate-800" onClick={() => setStatusFilter([])}>Clear</button>
                <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700" onClick={() => setStatusFilterOpen(false)}>Done</Button>
              </div>
            </PopoverContent>
          </Popover>
          <Input placeholder="Search customer…" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="h-8 w-40 text-xs" data-testid="books-customer-search" />
          <AdvancedFilterBuilder rows={advRows} setRows={setAdvRows} allSalespersons={meta.salespersons} onApply={fetchAll} />
          <button
            type="button"
            onClick={() => setExcludeRejected(!excludeRejected)}
            data-testid="toggle-rejected"
            title="Rejected invoices are excluded by default so reports show clean actual sales"
            className={`text-[11px] px-2.5 py-1.5 rounded-md border transition ${excludeRejected ? 'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium' : 'bg-amber-50 border-amber-300 text-amber-700 font-medium'}`}
          >
            {excludeRejected ? '✓ Rejected excluded' : '⚠ Rejected included'}
          </button>
          <button
            type="button"
            onClick={() => openInvoiceDrill({ title: 'Rejected invoices', subtitle: `${range.label} · status = rejected · excluded from all report numbers`, extraParams: { status: JSON.stringify(['rejected']), include_rejected: '1' } })}
            data-testid="view-rejected-btn"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          >
            View rejected
          </button>
          <div className="flex-1" />
          <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 rounded-md" onClick={fetchAllAndPmt} data-testid="books-run">Run Report</Button>
        </Card>

        <SavedFilters
          storageKey="booksOverviewSavedFilters"
          getCurrent={() => ({
            preset, specificFy, specificMonth,
            customFrom: custom?.from ? fmt(custom.from) : null, customTo: custom?.to ? fmt(custom.to) : null,
            clientType, statusFilter, spState, customerSearch, excludeRejected,
          })}
          onApply={(f) => {
            if (f.preset) setPreset(f.preset);
            if (f.specificFy !== undefined && f.specificFy !== null) setSpecificFy(f.specificFy);
            if (f.specificMonth !== undefined && f.specificMonth !== null) setSpecificMonth(f.specificMonth);
            if (f.customFrom || f.customTo) setCustom({ from: f.customFrom ? new Date(f.customFrom) : null, to: f.customTo ? new Date(f.customTo) : null });
            setClientType(f.clientType ?? 'all');
            setStatusFilter(f.statusFilter ?? []);
            setSpState(f.spState ?? { op: 'is in', selected: [], group: false });
            setCustomerSearch(f.customerSearch ?? '');
            setExcludeRejected(f.excludeRejected ?? true);
          }}
        />

        {/* Active chips */}
        <div className="flex items-center flex-wrap gap-1.5 text-[11px]">
          <span className="text-slate-400">Active filters:</span>
          <span className="bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-700">
            {range.label} · {fmt(range.from)} → {fmt(range.to)}
          </span>
          {clientType !== 'all' && <span className="bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5 flex items-center gap-1">Client: {clientType} <X className="w-3 h-3 cursor-pointer" onClick={() => setClientType('all')} /></span>}
          {statusFilter.length > 0 && <span className="bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5 flex items-center gap-1">Status: {statusFilter.join(', ')} <X className="w-3 h-3 cursor-pointer" onClick={() => setStatusFilter([])} /></span>}
          {spState.selected.length > 0 && <span className="bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5 flex items-center gap-1">Salesperson {spState.op} {spState.selected.length}{spState.group && ' · Grouped'} <X className="w-3 h-3 cursor-pointer" onClick={() => setSpState({ op: 'is in', selected: [], group: false })} /></span>}
          {collectionMode && <span className="bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5 flex items-center gap-1 font-semibold">🔥 Collection Mode · Aged &gt; 45d <X className="w-3 h-3 cursor-pointer" onClick={clearCollectionMode} /></span>}
          {voidState.count > 0 && (
            <button
              onClick={() => setVoidState((s) => ({ ...s, open: true }))}
              data-testid="books-void-pill"
              title={`${voidState.count} void invoices worth ₹${Math.round(voidState.total_voided_value).toLocaleString('en-IN')} — excluded from all reports below.`}
              className="bg-slate-100 text-slate-700 border border-slate-300 rounded-full px-2 py-0.5 flex items-center gap-1 hover:bg-slate-200"
            >
              <Ban className="w-3 h-3" /> Void: {voidState.count} <span className="text-slate-400">· excluded</span>
            </button>
          )}
          {advRows.filter(r => r.field && r.op).map((r) => (
            <span key={r.id} className="bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 flex items-center gap-1">
              {r.field} {r.op} {Array.isArray(r.value) ? `[${r.value.length}]` : (r.value || '—')}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setAdvRows((p) => p.filter(x => x.id !== r.id))} />
            </span>
          ))}
          <span className="ml-auto text-slate-400 flex items-center gap-1"><Info className="w-3 h-3" /> Last Zoho sync · {meta.last_sync ? new Date(meta.last_sync).toLocaleString() : '—'}</span>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
            <TabsTrigger value="overview" className="text-xs" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="aging" className="text-xs" data-testid="tab-aging">AR Aging</TabsTrigger>
            <TabsTrigger value="salesperson" className="text-xs" data-testid="tab-salesperson">By Salesperson</TabsTrigger>
            <TabsTrigger value="clienttype" className="text-xs" data-testid="tab-clienttype">By Client Type</TabsTrigger>
            <TabsTrigger value="payments" className="text-xs" data-testid="tab-payments">Payments Received</TabsTrigger>
            <TabsTrigger value="topcustomers" className="text-xs" data-testid="tab-topcustomers">Top Customers</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <Card className="p-5 border border-border">
              <div className="flex items-center justify-between mb-3">
                <div><h3 className="text-sm font-semibold text-slate-900">Billing vs Collections</h3><p className="text-[11px] text-slate-500">{range.label} · click Billing / Pending for invoices, Collections for payments</p></div>
                <span className="text-[10px] text-slate-400">{billCol.rows.length} month(s)</span>
              </div>
              {loading && billCol.rows.length === 0 ? (
                <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-violet-600" /></div>
              ) : billCol.rows.length === 0 ? (
                <p className="text-xs text-slate-400 py-8 text-center">No billing or collections in this window.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="billing-collections-table">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                        <th className="text-left px-2.5 py-2 font-semibold">Month</th>
                        <th className="text-right px-2.5 py-2 font-semibold">Billing</th>
                        <th className="text-right px-2.5 py-2 font-semibold">Collections</th>
                        <th className="text-right px-2.5 py-2 font-semibold">Current Billing Collection</th>
                        <th className="text-right px-2.5 py-2 font-semibold">Previous Invoice Collection</th>
                        <th className="text-right px-2.5 py-2 font-semibold">Pending Collection</th>
                        <th className="text-right px-2.5 py-2 font-semibold">Coll %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billCol.rows.map((r) => {
                        const [yy, mm] = r.month.split('-').map(Number);
                        const mLabel = new Date(yy, mm - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
                        return (
                          <tr key={r.month} className="border-t border-slate-100 hover:bg-violet-50/40" data-testid={`billcol-row-${r.month}`}>
                            <td className="px-2.5 py-2 font-medium text-slate-800 whitespace-nowrap">
                              {mLabel} <span className="ml-1 text-[10px] bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">{r.inv_count} inv</span>
                            </td>
                            <td className="text-right px-2.5 py-2 tabular-nums font-semibold text-violet-700 cursor-pointer hover:underline" title="Invoices raised this month — click to view"
                              onClick={() => drillMonthInvoices(r.month)} data-testid={`billcol-billing-${r.month}`}>{fmtInr(r.billing)}</td>
                            <td className="text-right px-2.5 py-2 tabular-nums font-semibold text-emerald-700 cursor-pointer hover:underline" title="Cash received this month — click to view payments"
                              onClick={() => drillMonthPayments(r.month, 'Collections')} data-testid={`billcol-collections-${r.month}`}>{fmtInr(r.collections)}</td>
                            <td className="text-right px-2.5 py-2 tabular-nums text-cyan-700 cursor-pointer hover:underline" title="Of Collections, portion applied against invoices raised in this same month — click for payments"
                              onClick={() => drillMonthPayments(r.month, 'Current Billing Collection')} data-testid={`billcol-current-${r.month}`}>{fmtInr(r.current_collection)}</td>
                            <td className="text-right px-2.5 py-2 tabular-nums text-slate-500 cursor-pointer hover:underline" title="Of Collections, portion applied against older invoices — click for payments"
                              onClick={() => drillMonthPayments(r.month, 'Previous Invoice Collection')} data-testid={`billcol-previous-${r.month}`}>{fmtInr(r.previous_collection)}</td>
                            <td className="text-right px-2.5 py-2 tabular-nums text-amber-700 cursor-pointer hover:underline" title="Still-open portion of this month's Billing — click to view invoices"
                              onClick={() => drillMonthInvoices(r.month, true)} data-testid={`billcol-pending-${r.month}`}>{fmtInr(r.pending)}</td>
                            <td className="text-right px-2.5 py-2 tabular-nums text-slate-500">{r.coll_pct}%</td>
                          </tr>
                        );
                      })}
                      {billCol.totals && (
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold" data-testid="billcol-total-row">
                          <td className="px-2.5 py-2 text-slate-800">GRAND TOTAL <span className="ml-1 text-[10px] font-normal text-slate-500">{billCol.totals.inv_count} inv</span></td>
                          <td className="text-right px-2.5 py-2 tabular-nums text-violet-700">{fmtInr(billCol.totals.billing)}</td>
                          <td className="text-right px-2.5 py-2 tabular-nums text-emerald-700">{fmtInr(billCol.totals.collections)}</td>
                          <td className="text-right px-2.5 py-2 tabular-nums text-cyan-700">{fmtInr(billCol.totals.current_collection)}</td>
                          <td className="text-right px-2.5 py-2 tabular-nums text-slate-600">{fmtInr(billCol.totals.previous_collection)}</td>
                          <td className="text-right px-2.5 py-2 tabular-nums text-amber-700">{fmtInr(billCol.totals.pending)}</td>
                          <td className="text-right px-2.5 py-2 tabular-nums">{billCol.totals.coll_pct}%</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                <b className="text-violet-700">Billing</b> = invoices raised that month &nbsp;·&nbsp;
                <b className="text-emerald-700">Collections</b> = cash received that month &nbsp;·&nbsp;
                <b className="text-cyan-700">Current Billing Collection</b> = of Collections, portion paid against same-month invoices &nbsp;·&nbsp;
                <b className="text-slate-600">Previous Invoice Collection</b> = of Collections, portion paid against older invoices &nbsp;·&nbsp;
                <b className="text-amber-700">Pending Collection</b> = still-open portion of that month&apos;s Billing &nbsp;·&nbsp;
                <b>Coll %</b> = (Billing − Pending) ÷ Billing
              </p>
            </Card>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-5 border border-border">
                <div className="flex items-center gap-1.5 mb-3"><BarChart3 className="w-3.5 h-3.5 text-violet-600" /><h3 className="text-sm font-semibold text-slate-900">Sales by Client Type</h3><span className="ml-auto text-[10px] text-slate-400">Click a row →</span></div>
                <div className="space-y-3">
                  {byCt.map((r, i) => (
                    <button
                      key={r.client_type}
                      type="button"
                      onClick={() => openInvoiceDrill({ title: `Client Type: ${r.client_type}`, subtitle: `${range.label} · ${r.invoices} invoices totaling ${fmtInr(r.invoiced)}`, extraParams: { client_type: r.client_type } })}
                      data-testid={`overview-ct-row-${r.client_type}`}
                      className="w-full text-left hover:bg-slate-50 -mx-2 px-2 py-1 rounded-md transition"
                    >
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-slate-800">{r.client_type} <span className="text-slate-400 font-normal">· {r.invoices} inv</span></span>
                        <span className="text-slate-700 font-semibold">{fmtInr(r.invoiced)} <span className="text-slate-400 font-normal">({r.pct}%)</span></span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={i === 0 ? 'h-full bg-violet-500' : i === 1 ? 'h-full bg-indigo-500' : 'h-full bg-slate-400'} style={{ width: `${r.pct}%` }} /></div>
                    </button>
                  ))}
                </div>
              </Card>
              <Card className="p-5 border border-border">
                <div className="flex items-center gap-1.5 mb-3"><Wallet className="w-3.5 h-3.5 text-emerald-600" /><h3 className="text-sm font-semibold text-slate-900">Collection by Client Type</h3><span className="ml-auto text-[10px] text-slate-400">Click a value →</span></div>
                <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-slate-400 font-medium mb-2 pr-1">
                  <span>Client Type</span>
                  <span className="flex gap-2"><span className="w-24 text-right text-emerald-600">Cash-in</span><span className="w-24 text-right text-violet-600">Collected</span></span>
                </div>
                <div className="space-y-3" data-testid="overview-collection-by-ct">
                  {collCtRows.length === 0 ? (
                    <p className="text-xs text-slate-400 py-6 text-center">No collections in this window.</p>
                  ) : collCtRows.map((r) => {
                    const unassigned = r.client_type === '(Unassigned)';
                    return (
                      <div key={r.client_type} data-testid={`overview-collct-row-${r.client_type}`}>
                        <div className="flex items-center justify-between text-xs mb-1 pr-1">
                          <span className="font-medium text-slate-800">{r.client_type} <span className="text-slate-400 font-normal">· {r.payments} pmts</span></span>
                          <span className="flex gap-2">
                            <button
                              type="button"
                              disabled={unassigned}
                              onClick={() => openPmtDrill({ title: `Cash-in: ${r.client_type}`, subtitle: `${range.label} · ${r.payments} payments received`, extraParams: { client_type: r.client_type } })}
                              data-testid={`collct-cashin-${r.client_type}`}
                              title="Cash received in this window from this client type — click for payments"
                              className={`w-24 text-right tabular-nums font-semibold text-emerald-700 ${unassigned ? 'cursor-default' : 'hover:underline'}`}
                            >{fmtInr(r.cash_in)}</button>
                            <button
                              type="button"
                              disabled={unassigned}
                              onClick={() => openInvoiceDrill({ title: `Collected — ${r.client_type}`, subtitle: `${range.label} · paid portion of invoices raised in this window`, extraParams: { client_type: r.client_type } })}
                              data-testid={`collct-collected-${r.client_type}`}
                              title="Paid portion of invoices raised in this window — click for invoices"
                              className={`w-24 text-right tabular-nums font-semibold text-violet-700 ${unassigned ? 'cursor-default' : 'hover:underline'}`}
                            >{fmtInr(r.collected)}</button>
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${r.pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                  <b className="text-emerald-700">Cash-in</b> = payments received in this window (bar = share of cash-in) &nbsp;·&nbsp;
                  <b className="text-violet-700">Collected</b> = paid portion of invoices raised in this window
                </p>
              </Card>
              <Card className="p-5 border border-border lg:col-span-2">
                <div className="flex items-center gap-1.5 mb-3"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /><h3 className="text-sm font-semibold text-slate-900">AR Aging Snapshot</h3><span className="ml-auto text-[10px] text-slate-400">Click a bucket →</span></div>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${(aging.bucket_labels || []).length || 4}, minmax(0, 1fr))` }}>
                  {(aging.bucket_labels || []).map((label, i) => {
                    const tone = i === 0 ? 'emerald' : i === 1 ? 'amber' : 'red';
                    const b = aging.buckets?.[label] || { amount: 0, count: 0 };
                    const bounds = parseBucket(label);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => openInvoiceDrill({
                          title: `AR Aging: ${label} days overdue`,
                          subtitle: `${b.count} unpaid invoices · ${fmtInr(b.amount)} outstanding · all dates`,
                          extraParams: { min_aging_days: bounds.min ?? 0, max_aging_days: bounds.max ?? undefined, from_date: undefined, to_date: undefined },
                        })}
                        data-testid={`overview-aging-${label}`}
                        className={`rounded-md border p-2 text-center transition hover:brightness-95 hover:scale-[1.02] ${tone === 'emerald' ? 'border-emerald-100 bg-emerald-50' : tone === 'amber' ? 'border-amber-100 bg-amber-50' : 'border-red-100 bg-red-50'}`}
                      >
                        <p className={`text-[10px] font-medium ${tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : 'text-red-700'}`}>{label} days</p>
                        <p className="text-sm font-bold text-slate-900 mt-1">{fmtInr(b.amount)}</p>
                        <p className="text-[10px] text-slate-500">{b.count} inv</p>
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="aging" className="mt-4">
            <Card className="p-5 border border-border">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-slate-900">
                  Aging by {agingGroupBy === 'customer' ? 'Customer' : 'Salesperson'}
                  {agingGroupBy === 'salesperson' && spState.group && (
                    <Badge className="ml-2 bg-violet-100 text-violet-800 text-[10px]">Grouped view</Badge>
                  )}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-slate-500">Group by:</span>
                  <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                    <button onClick={() => setAgingGroupBy('customer')} data-testid="aging-groupby-customer"
                      className={`text-xs px-3 py-1 rounded ${agingGroupBy === 'customer' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'} transition`}>Customer</button>
                    <button onClick={() => setAgingGroupBy('salesperson')} data-testid="aging-groupby-salesperson"
                      className={`text-xs px-3 py-1 rounded ${agingGroupBy === 'salesperson' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'} transition`}>Salesperson</button>
                  </div>
                  <span className="text-[11px] text-slate-500 ml-2">Buckets (days):</span>
                  <Input value={customBuckets} onChange={(e) => setCustomBuckets(e.target.value)}
                    onBlur={fetchAllAndPmt} onKeyDown={(e) => e.key === 'Enter' && fetchAllAndPmt()}
                    placeholder="30,60,90" className="h-8 w-32 text-xs"
                    title="Comma-separated day cutoffs, e.g. 30,60,90,120"
                    data-testid="aging-buckets-input" />
                </div>
              </div>

              {/* Inline bucket tabs — like Zoho */}
              <div className="flex items-center gap-1 mb-3 border-b border-slate-200">
                <button onClick={() => setAgingBucketTab('all')}
                  className={`text-xs px-3 py-2 border-b-2 -mb-px transition ${agingBucketTab === 'all' ? 'border-violet-600 text-violet-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                  data-testid="aging-tab-all">
                  All <span className="ml-1 text-slate-400 text-[10px]">
                    ({fmtInr(Object.values(aging.buckets || {}).reduce((s, b) => s + (b.amount || 0), 0))})
                  </span>
                </button>
                {(aging.bucket_labels || []).map((label, i) => {
                  const b = aging.buckets?.[label] || { amount: 0, count: 0 };
                  const active = agingBucketTab === label;
                  const tone = i === 0 ? 'text-emerald-700' : i === 1 ? 'text-amber-700' : 'text-red-700';
                  return (
                    <button key={label} onClick={() => setAgingBucketTab(label)}
                      className={`text-xs px-3 py-2 border-b-2 -mb-px transition ${active ? 'border-violet-600 text-violet-700 font-medium' : `border-transparent ${tone} hover:text-slate-800`}`}
                      data-testid={`aging-tab-${label}`}>
                      {label}d <span className="ml-1 text-slate-400 text-[10px]">({b.count} · {fmtInr(b.amount)})</span>
                    </button>
                  );
                })}
              </div>

              {agingGroupBy === 'customer' ? (
                <div className="border border-slate-100 rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
                  <div className="grid gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium sticky top-0 z-10"
                    style={{ gridTemplateColumns: `2fr repeat(${(aging.bucket_labels || []).length}, 1fr) 1fr 1fr 30px` }}>
                    <div>Customer</div>
                    {(aging.bucket_labels || []).map((l) => <div key={l} className="text-right">{l}d</div>)}
                    <div className="text-right">Total Due</div>
                    <div className="text-right">Salesperson</div>
                    <div />
                  </div>
                  {aging.customers
                    .filter(r => agingBucketTab === 'all' || (r.buckets?.[agingBucketTab] || 0) > 0)
                    .map((r, i) => {
                      const drillKey = `${r.customer_id}::${agingBucketTab}`;
                      const isExpanded = expandedCustomer === r.customer_id;
                      const invoices = customerInvoices[drillKey] || [];
                      return (
                        <React.Fragment key={r.customer_id || i}>
                          <div className="grid gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center hover:bg-violet-50 cursor-pointer"
                            style={{ gridTemplateColumns: `2fr repeat(${(aging.bucket_labels || []).length}, 1fr) 1fr 1fr 30px` }}
                            onClick={() => {
                              const next = isExpanded ? null : r.customer_id;
                              setExpandedCustomer(next);
                              if (next) fetchCustomerInvoices(r.customer_id, r.customer_name, agingBucketTab);
                            }}
                            data-testid={`aging-cust-row-${i}`}>
                            <div className="font-medium text-slate-800 truncate flex items-center gap-1">
                              {isExpanded ? <ChevronDown className="w-3 h-3 text-violet-500" /> : <ChevronDown className="w-3 h-3 text-slate-300 -rotate-90" />}
                              {r.customer_name}
                            </div>
                            {(aging.bucket_labels || []).map((l, idx) => {
                              const v = r.buckets?.[l] || 0;
                              const tone = idx === 0 ? 'text-slate-700' : idx === 1 ? 'text-amber-700' : 'text-red-700';
                              return <div key={l} className={`text-right ${tone}`}>{v ? fmtInr(v) : '—'}</div>;
                            })}
                            <div className="text-right font-semibold text-slate-900">{fmtInr(r.total_due)}</div>
                            <div className="text-right text-slate-500 truncate">{r.salesperson_name || '—'}</div>
                            <div />
                          </div>
                          {isExpanded && (
                            <div className="bg-slate-50/50 border-t border-slate-100 px-4 py-3" data-testid={`aging-cust-drill-${i}`}>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-[11px] font-semibold text-slate-700">Invoices for {r.customer_name} · {invoices.length} shown</p>
                                <span className="text-[10px] text-slate-400">Click any row to open in Zoho Books ↗</span>
                              </div>
                              {invoices.length === 0 ? (
                                <p className="text-xs text-slate-400 py-2">Loading…</p>
                              ) : (
                                <div className="bg-white border border-slate-200 rounded overflow-hidden">
                                  <div className="grid grid-cols-9 gap-2 px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                                    <div className="col-span-2">Inv #</div><div>Date</div><div>Due</div><div className="text-right">Total</div>
                                    <div className="text-right">Balance</div><div>Status</div><div>Curr</div><div />
                                  </div>
                                  {invoices.map((inv) => {
                                    const url = zohoInvoiceUrl(inv.invoice_id);
                                    return (
                                      <div key={inv.invoice_id} className="grid grid-cols-9 gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-violet-50">
                                        <div className="col-span-2 font-medium text-slate-700 whitespace-nowrap">{inv.invoice_number || '—'}</div>
                                        <div className="text-slate-500">{inv.date}</div>
                                        <div className="text-slate-500">{inv.due_date}</div>
                                        <div className="text-right text-slate-700">{fmtCurrency(inv.total, inv.currency_code)}</div>
                                        <div className={`text-right font-medium ${inv.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtCurrency(inv.balance, inv.currency_code)}</div>
                                        <div><Badge className="bg-slate-100 text-slate-700 text-[9px] py-0">{inv.status}</Badge></div>
                                        <div className="text-slate-400">{inv.currency_code || 'INR'}</div>
                                        <div>{url && <a href={url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline text-[10px] font-medium" onClick={(e) => e.stopPropagation()}>Open ↗</a>}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  {aging.customers.length === 0 && !loading && <p className="text-xs text-slate-400 text-center py-8">No outstanding invoices match current filters.</p>}
                </div>
              ) : (
                <div className="border border-slate-100 rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
                  <div className="grid gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium sticky top-0 z-10"
                    style={{ gridTemplateColumns: `2fr repeat(${(aging.bucket_labels || []).length}, 1fr) 1fr 1fr` }}>
                    <div>Salesperson</div>
                    {(aging.bucket_labels || []).map((l) => <div key={l} className="text-right">{l}d</div>)}
                    <div className="text-right">Total Due</div>
                    <div className="text-right">Cust · Inv</div>
                  </div>
                  {aging.salespersons
                    .filter(r => agingBucketTab === 'all' || (r.buckets?.[agingBucketTab] || 0) > 0)
                    .map((r, i) => {
                      const drillKey = `${r.salesperson_name}::${agingBucketTab}`;
                      const isExpanded = expandedSalesperson === r.salesperson_name;
                      const invoices = salespersonInvoices[drillKey] || [];
                      return (
                        <React.Fragment key={r.salesperson_name || i}>
                          <div className="grid gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center hover:bg-violet-50 cursor-pointer"
                            style={{ gridTemplateColumns: `2fr repeat(${(aging.bucket_labels || []).length}, 1fr) 1fr 1fr` }}
                            onClick={() => {
                              if (r.is_group) return;
                              const next = isExpanded ? null : r.salesperson_name;
                              setExpandedSalesperson(next);
                              if (next) fetchSalespersonInvoices(r.salesperson_name, agingBucketTab);
                            }}
                            data-testid={`aging-sp-row-${i}`}>
                            <div className="font-medium text-slate-800 truncate flex items-center gap-1">
                              {!r.is_group && (isExpanded ? <ChevronDown className="w-3 h-3 text-violet-500" /> : <ChevronDown className="w-3 h-3 text-slate-300 -rotate-90" />)}
                              {r.is_group && <Layers className="w-3.5 h-3.5 text-violet-500" />}
                              {r.salesperson_name}
                              {r.is_group && <span className="text-slate-400 font-normal text-[10px] ml-1">({r.members.slice(0, 2).join(', ')}{r.members.length > 2 ? ` +${r.members.length - 2}` : ''})</span>}
                            </div>
                            {(aging.bucket_labels || []).map((l, idx) => {
                              const v = r.buckets?.[l] || 0;
                              const tone = idx === 0 ? 'text-slate-700' : idx === 1 ? 'text-amber-700' : 'text-red-700';
                              return <div key={l} className={`text-right ${tone}`}>{v ? fmtInr(v) : '—'}</div>;
                            })}
                            <div className="text-right font-semibold text-slate-900">{fmtInr(r.total_due)}</div>
                            <div className="text-right text-slate-500">{r.customer_count || 0} · {r.count}</div>
                          </div>
                          {isExpanded && (
                            <div className="bg-slate-50/50 border-t border-slate-100 px-4 py-3" data-testid={`aging-sp-drill-${i}`}>
                              <p className="text-[11px] font-semibold text-slate-700 mb-2">Invoices booked by {r.salesperson_name} · {invoices.length} shown</p>
                              {invoices.length === 0 ? (
                                <p className="text-xs text-slate-400 py-2">Loading…</p>
                              ) : (
                                <div className="bg-white border border-slate-200 rounded overflow-hidden">
                                  <div className="grid grid-cols-9 gap-2 px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                                    <div className="col-span-2">Inv #</div><div>Customer</div><div>Date</div><div className="text-right">Total</div>
                                    <div className="text-right">Balance</div><div>Status</div><div>Curr</div><div />
                                  </div>
                                  {invoices.slice(0, 100).map((inv) => {
                                    const url = zohoInvoiceUrl(inv.invoice_id);
                                    return (
                                      <div key={inv.invoice_id} className="grid grid-cols-9 gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-violet-50">
                                        <div className="col-span-2 font-medium text-slate-700 whitespace-nowrap">{inv.invoice_number || '—'}</div>
                                        <div className="text-slate-600 truncate">{inv.customer_name}</div>
                                        <div className="text-slate-500">{inv.date}</div>
                                        <div className="text-right text-slate-700">{fmtCurrency(inv.total, inv.currency_code)}</div>
                                        <div className={`text-right font-medium ${inv.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtCurrency(inv.balance, inv.currency_code)}</div>
                                        <div><Badge className="bg-slate-100 text-slate-700 text-[9px] py-0">{inv.status}</Badge></div>
                                        <div className="text-slate-400">{inv.currency_code || 'INR'}</div>
                                        <div>{url && <a href={url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline text-[10px] font-medium" onClick={(e) => e.stopPropagation()}>Open ↗</a>}</div>
                                      </div>
                                    );
                                  })}
                                  {invoices.length > 100 && <p className="text-[10px] text-slate-400 px-3 py-2 border-t">Showing 100 of {invoices.length} — refine filters to see more</p>}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  {aging.salespersons.length === 0 && !loading && <p className="text-xs text-slate-400 text-center py-8">No outstanding invoices match current filters.</p>}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="salesperson" className="mt-4">
            <Card className="p-5 border border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-violet-600" />
                  <h3 className="text-sm font-semibold text-slate-900">Salesperson Leaderboard</h3>
                  {spState.group && <Badge className="ml-2 bg-violet-100 text-violet-800 text-[10px]">Grouped view</Badge>}
                </div>
                <span className="text-[11px] text-slate-400">{bySp.length} row(s) · click a row to see invoices</span>
              </div>
              <div className="border border-slate-100 rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div>#</div><div className="col-span-2">Salesperson</div>
                  <div className="text-right">Billing</div><div className="text-right">Collections</div>
                  <div className="text-right col-span-2">Current Billing Collection</div>
                  <div className="text-right col-span-2">Previous Invoice Collection</div>
                  <div className="text-right">Pending Collection</div><div className="text-right">Coll %</div><div className="text-right">Inv</div>
                </div>
                {bySp.map((s, i) => (
                  <button
                    key={s.salesperson_name}
                    type="button"
                    onClick={() => openInvoiceDrill({
                      title: `Salesperson: ${s.salesperson_name}`,
                      subtitle: `${range.label} · ${s.invoices} invoices · ${fmtInr(s.invoiced)} billed`,
                      extraParams: s.is_group
                        ? { salesperson_names: JSON.stringify(s.members), salesperson_op: 'is in' }
                        : { salesperson_names: JSON.stringify([s.salesperson_name]), salesperson_op: 'is in' },
                    })}
                    className="w-full grid grid-cols-12 gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center hover:bg-violet-50 text-left transition"
                    data-testid={`sp-row-${i}`}
                  >
                    <div className="flex items-center gap-1 font-bold text-slate-800">
                      {s.is_group ? <Layers className="w-3.5 h-3.5 text-violet-500" /> : (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`)}
                    </div>
                    <div className="col-span-2 font-medium text-slate-800 truncate">
                      {s.salesperson_name}
                      {s.is_group && <span className="text-slate-400 font-normal text-[10px] ml-1">({s.members.slice(0, 2).join(', ')}{s.members.length > 2 ? ` +${s.members.length - 2}` : ''})</span>}
                    </div>
                    <div className="text-right tabular-nums font-semibold text-violet-700">{fmtInr(s.invoiced)}</div>
                    <div className="text-right tabular-nums font-semibold text-emerald-700">{fmtInr(s.collections ?? 0)}</div>
                    <div className="text-right tabular-nums text-cyan-700 col-span-2">{fmtInr(s.current_collection ?? 0)}</div>
                    <div className="text-right tabular-nums text-slate-500 col-span-2">{fmtInr(s.previous_collection ?? 0)}</div>
                    <div className="text-right tabular-nums text-amber-700">{fmtInr(s.outstanding)}</div>
                    <div className="text-right tabular-nums text-slate-500">{s.coll_pct ?? 0}%</div>
                    <div className="text-right text-slate-500">{s.invoices}</div>
                  </button>
                ))}
                {bySp.length > 1 && (
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-t-2 border-slate-300 text-xs items-center font-bold" data-testid="sp-total-row">
                    <div className="col-span-3 text-slate-800">GRAND TOTAL</div>
                    <div className="text-right tabular-nums text-violet-700">{fmtInr(bySp.reduce((a, r) => a + (r.invoiced || 0), 0))}</div>
                    <div className="text-right tabular-nums text-emerald-700">{fmtInr(bySp.reduce((a, r) => a + (r.collections || 0), 0))}</div>
                    <div className="text-right tabular-nums text-cyan-700 col-span-2">{fmtInr(bySp.reduce((a, r) => a + (r.current_collection || 0), 0))}</div>
                    <div className="text-right tabular-nums text-slate-600 col-span-2">{fmtInr(bySp.reduce((a, r) => a + (r.previous_collection || 0), 0))}</div>
                    <div className="text-right tabular-nums text-amber-700">{fmtInr(bySp.reduce((a, r) => a + (r.outstanding || 0), 0))}</div>
                    <div className="text-right tabular-nums text-slate-600">{(() => { const b = bySp.reduce((a, r) => a + (r.invoiced || 0), 0); const pnd = bySp.reduce((a, r) => a + (r.outstanding || 0), 0); return b > 0 ? Math.round(Math.max(b - pnd, 0) / b * 1000) / 10 : 0; })()}%</div>
                    <div className="text-right text-slate-600">{bySp.reduce((a, r) => a + (r.invoices || 0), 0)}</div>
                  </div>
                )}
                {bySp.length === 0 && !loading && <p className="text-xs text-slate-400 text-center py-8">No data.</p>}
              </div>
              {columnsFootnote('salesperson')}
            </Card>
          </TabsContent>

          <TabsContent value="clienttype" className="mt-4">
            <Card className="p-5 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900">By Client Type</h3>
                <span className="text-[11px] text-slate-400">Click a row to see contributing invoices</span>
              </div>
              <div className="border border-slate-100 rounded-lg overflow-hidden">
                <div className="grid grid-cols-11 gap-2 px-3 py-2 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div className="col-span-2">Client Type</div><div className="text-right">Inv</div>
                  <div className="text-right">Billing</div><div className="text-right">Collections</div>
                  <div className="text-right col-span-2">Current Billing Collection</div>
                  <div className="text-right col-span-2">Previous Invoice Collection</div>
                  <div className="text-right">Pending Collection</div><div className="text-right">Coll %</div>
                </div>
                {byCt.map((r) => {
                  const isExpanded = expandedClientType === r.client_type;
                  const invoices = clientTypeInvoices[r.client_type] || [];
                  return (
                    <React.Fragment key={r.client_type}>
                      <div className="grid grid-cols-11 gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center hover:bg-violet-50 cursor-pointer"
                        onClick={() => {
                          const next = isExpanded ? null : r.client_type;
                          setExpandedClientType(next);
                          if (next) fetchClientTypeInvoices(r.client_type);
                        }}
                        data-testid={`ct-row-${r.client_type}`}>
                        <div className="col-span-2 font-medium text-slate-800 flex items-center gap-1">
                          {isExpanded ? <ChevronDown className="w-3 h-3 text-violet-500" /> : <ChevronDown className="w-3 h-3 text-slate-300 -rotate-90" />}
                          {r.client_type}
                        </div>
                        <div className="text-right text-slate-500">{r.invoices}</div>
                        <div className="text-right tabular-nums font-semibold text-violet-700">{fmtInr(r.invoiced)}</div>
                        <div className="text-right tabular-nums font-semibold text-emerald-700">{fmtInr(r.collections ?? 0)}</div>
                        <div className="text-right tabular-nums text-cyan-700 col-span-2">{fmtInr(r.current_collection ?? 0)}</div>
                        <div className="text-right tabular-nums text-slate-500 col-span-2">{fmtInr(r.previous_collection ?? 0)}</div>
                        <div className="text-right tabular-nums text-amber-700">{fmtInr(r.outstanding)}</div>
                        <div className="text-right tabular-nums text-slate-500">{r.coll_pct ?? 0}%</div>
                      </div>
                      {isExpanded && (
                        <div className="bg-slate-50/50 border-t border-slate-100 px-4 py-3" data-testid={`ct-drill-${r.client_type}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[11px] font-semibold text-slate-700">Invoices for client type: {r.client_type} · {invoices.length} shown</p>
                            <span className="text-[10px] text-slate-400">Click any row to open in Zoho Books ↗</span>
                          </div>
                          {invoices.length === 0 ? (
                            <p className="text-xs text-slate-400 py-2">Loading…</p>
                          ) : (
                            <div className="bg-white border border-slate-200 rounded overflow-hidden">
                              <div className="grid grid-cols-9 gap-2 px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                                <div className="col-span-2">Inv #</div><div>Customer</div><div>Date</div><div className="text-right">Total</div>
                                <div className="text-right">Balance</div><div>Status</div><div>Curr</div><div />
                              </div>
                              {invoices.slice(0, 100).map((inv) => {
                                const url = zohoInvoiceUrl(inv.invoice_id);
                                return (
                                  <div key={inv.invoice_id} className="grid grid-cols-9 gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-violet-50">
                                    <div className="col-span-2 font-medium text-slate-700 whitespace-nowrap">{inv.invoice_number || '—'}</div>
                                    <div className="text-slate-600 truncate">{inv.customer_name}</div>
                                    <div className="text-slate-500">{inv.date}</div>
                                    <div className="text-right text-slate-700">{fmtCurrency(inv.total, inv.currency_code)}</div>
                                    <div className={`text-right font-medium ${inv.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtCurrency(inv.balance, inv.currency_code)}</div>
                                    <div><Badge className="bg-slate-100 text-slate-700 text-[9px] py-0">{inv.status}</Badge></div>
                                    <div className="text-slate-400">{inv.currency_code || 'INR'}</div>
                                    <div>{url && <a href={url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline text-[10px] font-medium" onClick={(e) => e.stopPropagation()}>Open ↗</a>}</div>
                                  </div>
                                );
                              })}
                              {invoices.length > 100 && <p className="text-[10px] text-slate-400 px-3 py-2 border-t">Showing 100 of {invoices.length} — refine filters to see more</p>}
                            </div>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
                {byCt.length > 1 && (
                  <div className="grid grid-cols-11 gap-2 px-3 py-2 bg-slate-50 border-t-2 border-slate-300 text-xs items-center font-bold" data-testid="ct-total-row">
                    <div className="col-span-2 text-slate-800">GRAND TOTAL</div>
                    <div className="text-right text-slate-600">{byCt.reduce((a, r) => a + (r.invoices || 0), 0)}</div>
                    <div className="text-right tabular-nums text-violet-700">{fmtInr(byCt.reduce((a, r) => a + (r.invoiced || 0), 0))}</div>
                    <div className="text-right tabular-nums text-emerald-700">{fmtInr(byCt.reduce((a, r) => a + (r.collections || 0), 0))}</div>
                    <div className="text-right tabular-nums text-cyan-700 col-span-2">{fmtInr(byCt.reduce((a, r) => a + (r.current_collection || 0), 0))}</div>
                    <div className="text-right tabular-nums text-slate-600 col-span-2">{fmtInr(byCt.reduce((a, r) => a + (r.previous_collection || 0), 0))}</div>
                    <div className="text-right tabular-nums text-amber-700">{fmtInr(byCt.reduce((a, r) => a + (r.outstanding || 0), 0))}</div>
                    <div className="text-right tabular-nums text-slate-600">{(() => { const b = byCt.reduce((a, r) => a + (r.invoiced || 0), 0); const pnd = byCt.reduce((a, r) => a + (r.outstanding || 0), 0); return b > 0 ? Math.round(Math.max(b - pnd, 0) / b * 1000) / 10 : 0; })()}%</div>
                  </div>
                )}
              </div>
              {columnsFootnote('client type')}
            </Card>
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            {(() => {
              const s = pmtSyncStatus || {};
              const lastIso = s.last_sync_at;
              const mins = lastIso ? Math.floor((Date.now() - new Date(lastIso).getTime()) / 60000) : null;
              const freshCls = mins == null ? 'bg-slate-300' : mins < 60 ? 'bg-emerald-500' : mins < 1440 ? 'bg-amber-400' : 'bg-red-500';
              const freshLabel = mins == null ? 'No sync yet' : mins < 60 ? `Fresh · ${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
              const t = pmtData?.totals || {};
              return (
                <div className="space-y-4" data-testid="tab-payments-content">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="h-8 flex items-center gap-1.5 px-2.5 rounded-md border border-slate-200 bg-slate-50 text-xs" data-testid="pmt-sync-freshness" title="Payments are synced by the 'Sync from Zoho' button at the top (invoices + payments) and by the nightly auto-sync.">
                      <span className={`inline-block w-2 h-2 rounded-full ${freshCls}`}></span>
                      <span className="font-medium text-slate-600">{freshLabel}</span>
                      <span className="text-[10px] text-slate-400">· synced via &quot;Sync from Zoho&quot; above</span>
                    </span>
                    <span className="text-[10px] text-slate-400">Total cached: {s.total_payments ?? '—'}</span>
                    <button
                      onClick={async () => {
                        try {
                          toast.info('Enriching all pending payments · ~20s…');
                          const r = await api.post('/books/payments/quick-enrich');
                          toast.success(`Enriched ${r.data.enriched} payments in ${r.data.elapsed_seconds}s · ${r.data.api_calls_used} Zoho API calls · ${r.data.remaining} still pending`, { duration: 9000 });
                          fetchPmtData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Enrichment failed');
                        }
                      }}
                      className="h-8 flex items-center gap-1.5 px-2.5 rounded-md border border-violet-200 bg-violet-50 hover:bg-violet-100 text-xs text-violet-700"
                      data-testid="pmt-quick-enrich-header-btn"
                      title="Attribute every payment to its Category / Salesperson using Zoho's list-endpoint invoice_numbers field. Uses ~12 API calls for the entire cache. Safe to run anytime."
                    >
                      <Zap className="w-3 h-3" /> Quick enrich all
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          toast.info('Refreshing categories from current invoice cache · 0 Zoho calls…');
                          const r = await api.post('/books/payments/refresh-categories');
                          toast.success(`${r.data.refreshed} payments recategorised · ${r.data.unchanged} already correct · ${r.data.elapsed_seconds}s`, { duration: 10000 });
                          fetchPmtData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Refresh failed');
                        }
                      }}
                      className="h-8 flex items-center gap-1.5 px-2.5 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-xs text-emerald-700"
                      data-testid="pmt-refresh-categories-btn"
                      title="Force-recompute every payment's category attribution from the current invoice cache. Use this after cf_branch_name changes in Zoho. Uses zero Zoho API calls."
                    >
                      <Layers className="w-3 h-3" /> Refresh categories
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Full re-sync will re-pull every invoice from Zoho (ignoring last-modified-time). ~5-15 API calls. Continue?')) return;
                        try {
                          toast.info('Full invoice re-sync running · ~10s…');
                          const r = await api.post('/books/payments/full-resync-invoices');
                          toast.success(`Full resync done · ${r.data.invoices_refreshed} invoices refreshed · ${r.data.cf_branch_name_changes} category reclassifications · ${r.data.payments_recategorised} payments updated · ${r.data.api_calls_used} API calls`, { duration: 12000 });
                          fetchAllAndPmt();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Full resync failed');
                        }
                      }}
                      className="h-8 flex items-center gap-1.5 px-2.5 rounded-md border border-amber-200 bg-amber-50 hover:bg-amber-100 text-xs text-amber-800"
                      data-testid="pmt-full-resync-btn"
                      title="Belt-and-suspenders: force re-fetch every invoice from Zoho ignoring last_modified_time, then propagate category / salesperson changes to every payment. Use when incremental sync misses a custom-field-only edit. ~5-15 API calls."
                    >
                      <RefreshCw className="w-3 h-3" /> Full resync
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          toast.info('Scanning for currency mislabels · ~15s…');
                          const r = await api.post('/books/payments/fix-currency');
                          if (r.data.corrected > 0) {
                            toast.success(`${r.data.corrected} payments fixed · ${r.data.currency_code_resolved} currency codes resolved · ${r.data.api_calls_used} API calls · ${r.data.elapsed_seconds}s`, { duration: 10000 });
                          } else {
                            toast.success(`All ${r.data.unchanged} payments already accurate · ${r.data.api_calls_used} API calls used`, { duration: 6000 });
                          }
                          fetchPmtData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Currency fix failed');
                        }
                      }}
                      className="h-8 flex items-center gap-1.5 px-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs text-slate-600"
                      data-testid="pmt-fix-currency-btn"
                      title="Scan all payments and correct any mislabelled currency (foreign payments stored as INR). Uses the authoritative bcy_amount from Zoho's list endpoint plus a short detail-fetch for the real currency code (USD/EUR/etc)."
                    >
                      <IndianRupee className="w-3 h-3" /> Fix currency
                    </button>
                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-[10px] uppercase text-slate-500 font-semibold">Mode</span>
                      <select value={pmtMode} onChange={(e) => setPmtMode(e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-2 min-w-[180px]" data-testid="pmt-mode-filter">
                        <option value="all">All modes</option>
                        {pmtModes.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>

                  {pmtLoading ? (
                    <div className="p-10 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading payments…</div>
                  ) : !pmtData ? (
                    <Card className="p-5 border border-amber-200 bg-amber-50/40"><div className="flex items-start gap-3"><Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-slate-900">No payments in cache yet</p><p className="text-xs text-slate-600 mt-1">Click <b>Sync payments</b> above to fetch the payment history from Zoho Books.</p></div></div></Card>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card className="p-4 border" title="Sum of amount_bcy of all payments RECEIVED in this date range, regardless of which invoice's period they were applied to. Differs from the top 'Collected' KPI which measures how much of the CURRENT-PERIOD invoices has been paid.">
                          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Wallet className="w-4 h-4" /></div>
                          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Collected (Cash-in)</p>
                          <p className="text-2xl font-bold text-slate-900 mt-0.5">₹{Math.round(t.amount || 0).toLocaleString('en-IN')}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{t.count || 0} payments · cash-flow view</p>
                        </Card>
                        <Card className="p-4 border">
                          <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center"><IndianRupee className="w-4 h-4" /></div>
                          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Avg Ticket</p>
                          <p className="text-2xl font-bold text-slate-900 mt-0.5">₹{Math.round(t.avg_ticket || 0).toLocaleString('en-IN')}</p>
                        </Card>
                        <Card className="p-4 border">
                          <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Landmark className="w-4 h-4" /></div>
                          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Top Mode</p>
                          <p className="text-sm font-semibold text-slate-900 mt-1 truncate" title={t.top_mode}>{t.top_mode || '—'}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">₹{Math.round(t.top_mode_amount || 0).toLocaleString('en-IN')}</p>
                        </Card>
                        <Card className="p-4 border">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center"><Users className="w-4 h-4" /></div>
                          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Unique Modes</p>
                          <p className="text-2xl font-bold text-slate-900 mt-0.5">{pmtData?.by_mode?.length || 0}</p>
                        </Card>
                      </div>

                      <div className="flex items-center gap-1 border-b border-slate-200">
                        {[
                          { id: 'mode', label: 'By Mode' },
                          { id: 'category', label: 'By Category' },
                          { id: 'salesperson', label: 'By Salesperson' },
                          { id: 'customer', label: 'Top Customers' },
                          { id: 'list', label: `Payments (${pmtList.total})` },
                        ].map((s) => (
                          <button key={s.id} onClick={() => setPmtSubTab(s.id)} data-testid={`pmt-subtab-${s.id}`} className={`px-3 py-1.5 text-xs font-medium border-b-2 ${pmtSubTab === s.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{s.label}</button>
                        ))}
                      </div>

                      {(() => {
                        // Show a banner on the Category subtab when many payments lack the invoice-detail needed to attribute a Category
                        if (pmtSubTab !== 'category') return null;
                        const notMentioned = (pmtData.by_category || []).find(r => r.group === 'Not mentioned');
                        if (!notMentioned || !t.amount) return null;
                        const pct = (notMentioned.amount / t.amount) * 100;
                        if (pct < 5) return null;
                        return (
                          <Card className="p-3 border border-amber-200 bg-amber-50/60 flex items-start gap-3" data-testid="pmt-enrich-banner">
                            <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-slate-900">{pct.toFixed(1)}% of collections show &quot;Not mentioned&quot; category</p>
                              <p className="text-[11px] text-slate-600 mt-0.5">Their applied-invoice details haven&apos;t been pulled from Zoho yet. Run enrich to fetch invoice details in batches of 50 (respects Zoho&apos;s 60 req/min rate cap).</p>
                            </div>
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={async () => {
                              try {
                                toast.info('Enriching all pending payments · ~20s…');
                                const r = await api.post('/books/payments/quick-enrich');
                                toast.success(`Enriched ${r.data.enriched} payments in ${r.data.elapsed_seconds}s · only ${r.data.api_calls_used} Zoho API calls · ${r.data.remaining} still pending`, { duration: 9000 });
                                fetchPmtData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Enrichment failed');
                              }
                            }} data-testid="pmt-enrich-btn">Quick enrich all</Button>
                          </Card>
                        );
                      })()}

                      {(() => {
                        // Show attribution alert when Unassigned share is meaningful
                        const un = (pmtData.by_salesperson || []).find(r => r.group === '— Unassigned —');
                        const pct = un && t.amount ? (un.amount / t.amount) * 100 : 0;
                        if (pct < 5) return null;
                        return (
                          <Card className="p-3 border border-amber-200 bg-amber-50/60 flex items-start gap-3" data-testid="pmt-unassigned-alert">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-slate-900">{pct.toFixed(1)}% of collections have no salesperson attribution</p>
                              <p className="text-[11px] text-slate-600 mt-0.5">₹{Math.round(un.amount).toLocaleString('en-IN')} across {un.count} payments cannot be attributed. Run the backfill to infer salesperson from the customer&apos;s invoice history.</p>
                            </div>
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={async () => {
                              try {
                                const r = await api.post('/books/payments/backfill-salesperson');
                                toast.success(`Backfill complete · scanned ${r.data.scanned} · updated ${r.data.updated} · still unresolved ${r.data.still_unresolved}`, { duration: 8000 });
                                fetchPmtData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Backfill failed');
                              }
                            }} data-testid="pmt-backfill-btn">Run backfill</Button>
                          </Card>
                        );
                      })()}

                      {pmtSubTab !== 'list' ? (
                        <Card className="overflow-hidden">
                          <div className="grid grid-cols-6 gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                            <div className="col-span-3">{pmtSubTab === 'mode' ? 'Payment Mode' : pmtSubTab === 'salesperson' ? 'Salesperson' : pmtSubTab === 'category' ? 'Category' : 'Customer'}</div>
                            <div className="text-right">Payments</div><div className="text-right">Amount</div><div className="text-right">% of Total</div>
                          </div>
                          {(pmtData[`by_${pmtSubTab}`] || []).map((r) => {
                            const pct = t.amount ? (r.amount / t.amount) * 100 : 0;
                            const drillParams = pmtSubTab === 'mode' ? { mode: r.group }
                              : pmtSubTab === 'salesperson' ? (r.group === '— Unassigned —' ? { salesperson: '' } : { salesperson: r.group })
                              : pmtSubTab === 'category' ? { category: r.group }
                              : { customer_name: r.group };
                            return (
                              <button
                                key={r.group}
                                type="button"
                                onClick={() => openPmtDrill({
                                  title: `${pmtSubTab === 'mode' ? 'Payment Mode' : pmtSubTab === 'salesperson' ? 'Salesperson' : pmtSubTab === 'category' ? 'Category' : 'Customer'}: ${r.group}`,
                                  subtitle: `${r.count} payment${r.count === 1 ? '' : 's'} · ₹${Math.round(r.amount).toLocaleString('en-IN')} · ${range.label}`,
                                  extraParams: drillParams,
                                })}
                                className="w-full grid grid-cols-6 gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center hover:bg-violet-50 text-left transition"
                                data-testid={`pmt-row-${r.group}`}
                              >
                                <div className="col-span-3 font-medium text-slate-800 truncate">{r.group}</div>
                                <div className="text-right tabular-nums">{r.count}</div>
                                <div className="text-right tabular-nums font-semibold text-emerald-700">₹{Math.round(r.amount).toLocaleString('en-IN')}</div>
                                <div className="text-right tabular-nums text-slate-500">
                                  <div className="flex items-center gap-2 justify-end">
                                    <span>{pct.toFixed(1)}%</span>
                                    <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                      <div className="h-full bg-violet-500" style={{ width: `${Math.min(pct, 100)}%` }}></div>
                                    </div>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </Card>
                      ) : (
                        <Card className="overflow-hidden">
                          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                            <div>Date</div><div>Pmt #</div><div className="col-span-3">Customer</div><div className="col-span-2">Mode</div><div>Reference</div><div className="col-span-2">Applied to</div><div className="text-right">Salesperson</div><div className="text-right">Amount</div>
                          </div>
                          {(pmtList.payments || []).map((p) => (
                            <div key={p.payment_id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 text-[11px] items-center hover:bg-emerald-50" data-testid={`pmt-list-row-${p.payment_id}`}>
                              <div className="text-slate-500">{p.date}</div>
                              <div className="font-medium text-slate-700">{p.payment_number || '—'}</div>
                              <div className="col-span-3 text-slate-800 truncate">{p.customer_name}</div>
                              <div className="col-span-2 text-slate-600 truncate">{p.payment_mode}</div>
                              <div className="text-slate-500 truncate" title={p.reference_number}>{p.reference_number || '—'}</div>
                              <div className="col-span-2 text-slate-600 truncate">
                                {p.invoices?.slice(0, 2).map((inv, i) => (
                                  <a key={inv.invoice_id + '-' + i} href={`https://books.zoho.com/app/699857543/#/invoices/${inv.invoice_id}`} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline mr-1">{inv.invoice_number}↗</a>
                                ))}
                                {p.invoices?.length > 2 && <span className="text-slate-400">+{p.invoices.length - 2}</span>}
                              </div>
                              <div className="text-right text-slate-500 truncate">{p.salesperson_name || '—'}</div>
                              <div className="text-right tabular-nums font-semibold text-emerald-700">
                                ₹{Math.round(p.amount_bcy ?? (p.amount * (p.exchange_rate || 1))).toLocaleString('en-IN')}
                                {p.currency_code && p.currency_code !== 'INR' && (
                                  <div className="text-[9px] text-slate-400 font-normal">{p.currency_code} {Math.round(p.amount).toLocaleString('en-IN')} @ {p.exchange_rate?.toFixed(2)}</div>
                                )}
                              </div>
                            </div>
                          ))}
                          {pmtList.total > pmtList.payments.length && <div className="px-3 py-2 text-center text-[10px] text-slate-400 border-t border-slate-100">Showing first {pmtList.payments.length} of {pmtList.total}. Narrow filters to see more.</div>}
                        </Card>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="topcustomers" className="mt-4">
            <Card className="p-5 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900">Top {topCust.length} Customers by Revenue</h3>
                <span className="text-[11px] text-slate-400">Click a row to see the customer&apos;s invoices</span>
              </div>
              <div className="border border-slate-100 rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div>#</div><div className="col-span-2">Customer</div>
                  <div className="text-right">Billing</div><div className="text-right">Collections</div>
                  <div className="text-right col-span-2">Current Billing Collection</div>
                  <div className="text-right col-span-2">Previous Invoice Collection</div>
                  <div className="text-right">Pending Collection</div><div className="text-right">Coll %</div><div className="text-right">Inv</div>
                </div>
                {topCust.map((c, i) => (
                  <button
                    key={c.customer_id || i}
                    type="button"
                    onClick={() => openInvoiceDrill({
                      title: `Customer: ${c.customer_name}`,
                      subtitle: `${range.label} · ${c.invoices} invoices · ${fmtInr(c.invoiced)} billed`,
                      extraParams: c.customer_id ? { customer_id: c.customer_id } : { customer_search: c.customer_name },
                    })}
                    className="w-full grid grid-cols-12 gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center hover:bg-violet-50 text-left transition"
                    data-testid={`topcust-row-${i}`}
                  >
                    <div className="font-bold text-slate-500">#{i + 1}</div>
                    <div className="col-span-2 font-medium text-slate-800 truncate">{c.customer_name}</div>
                    <div className="text-right tabular-nums font-semibold text-violet-700">{fmtInr(c.invoiced)}</div>
                    <div className="text-right tabular-nums font-semibold text-emerald-700">{fmtInr(c.collections ?? 0)}</div>
                    <div className="text-right tabular-nums text-cyan-700 col-span-2">{fmtInr(c.current_collection ?? 0)}</div>
                    <div className="text-right tabular-nums text-slate-500 col-span-2">{fmtInr(c.previous_collection ?? 0)}</div>
                    <div className="text-right tabular-nums text-amber-700">{fmtInr(c.outstanding)}</div>
                    <div className="text-right tabular-nums text-slate-500">{c.coll_pct ?? 0}%</div>
                    <div className="text-right text-slate-500">{c.invoices}</div>
                  </button>
                ))}
              </div>
              {columnsFootnote('customer')}
            </Card>
          </TabsContent>
        </Tabs>

        {/* KPI Drilldown Dialog */}
        <CollectionPlanTrackerModal open={slipTrackerOpen} onOpenChange={setSlipTrackerOpen} zohoOrgId={zohoOrgId} />
        <PaymentsReceivedModal open={paymentsOpen} onOpenChange={setPaymentsOpen} zohoOrgId={zohoOrgId} />
        <PaymentsTrendModal open={paymentsTrendOpen} onOpenChange={setPaymentsTrendOpen} />
        <VoidPaymentsModal open={voidPaymentsOpen} onClose={() => setVoidPaymentsOpen(false)} />
        <Dialog open={kpiDrill.open} onOpenChange={(o) => setKpiDrill((p) => ({ ...p, open: o }))}>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="kpi-drill-dialog">
            <DialogHeader>
              <DialogTitle className="text-base font-semibold text-slate-900">{kpiDrill.title}</DialogTitle>
              <p className="text-[11px] text-slate-500 mt-0.5">{kpiDrill.subtitle} · {kpiDrill.total} invoice(s) total{kpiDrill.total > kpiDrill.invoices.length ? ` · showing first ${kpiDrill.invoices.length}` : ''}</p>
            </DialogHeader>
            <div className="flex items-center gap-1.5 pb-2 border-b border-slate-100">
              <Layers className="w-3 h-3 text-slate-400" />
              <span className="text-[10px] text-slate-500 mr-1">Group by</span>
              {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => { setKpiDrillGroup(v); setKpiDrillExpanded({}); }} data-testid={`kpi-drill-group-${v}`}
                  className={`text-[11px] px-2 py-1 rounded-md border transition ${kpiDrillGroup === v ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
              {kpiDrillGroup !== 'none' && (
                <>
                  <span className="w-px h-4 bg-slate-200 mx-1" />
                  <span className="text-[10px] text-slate-500">Then by</span>
                  {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type']].filter(([v]) => v === 'none' || v !== kpiDrillGroup).map(([v, l]) => (
                    <button key={v} type="button" onClick={() => { setKpiDrillGroup2(v); setKpiDrillExpanded({}); }} data-testid={`kpi-drill-group2-${v}`}
                      className={`text-[11px] px-2 py-1 rounded-md border transition ${kpiDrillGroup2 === v ? 'bg-violet-500 text-white border-violet-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
                  ))}
                </>
              )}
              {kpiDrillGroup !== 'none' && <span className="text-[10px] text-slate-400 ml-1">click a group to expand</span>}
            </div>
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              {kpiDrill.loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-violet-600" /></div>
              ) : kpiDrill.invoices.length === 0 ? (
                <p className="text-xs text-slate-400 py-8 text-center">No invoices match the filters.</p>
              ) : (
                <div className="bg-white border border-slate-200 rounded overflow-hidden">
                  <div className="grid grid-cols-11 gap-2 px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-medium sticky top-0">
                    <div className="col-span-2">Inv #</div><div className="col-span-2">Customer</div><div>Date</div>
                    <div>Expected</div>
                    <div className="text-right">Total</div><div className="text-right">Balance</div>
                    <div>Status</div><div>Salesperson</div><div />
                  </div>
                  {kpiDrillGroups === null
                    ? kpiDrill.invoices.map((inv) => renderKpiInvRow(inv))
                    : kpiDrillGroups.map((g) => (
                      <React.Fragment key={g.key}>
                        <button
                          type="button"
                          onClick={() => setKpiDrillExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))}
                          className="w-full grid grid-cols-11 gap-2 px-3 py-1.5 bg-violet-50/70 border-t border-violet-100 text-[11px] items-center font-semibold text-left hover:bg-violet-100/70 transition cursor-pointer"
                          data-testid={`kpi-drill-grp-${g.key}`}
                        >
                          <div className="col-span-6 text-violet-900 truncate flex items-center gap-1">
                            <ChevronDown className={`w-3 h-3 shrink-0 text-violet-500 transition-transform ${kpiDrillExpanded[g.key] ? '' : '-rotate-90'}`} />
                            {g.key} <span className="text-[10px] font-normal text-slate-500">· {g.invoices.length} inv</span>
                          </div>
                          <div className="text-right text-violet-800 tabular-nums">{fmtInr(g.total)}</div>
                          <div className={`text-right tabular-nums ${g.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(g.balance)}</div>
                          <div className="col-span-3" />
                        </button>
                        {kpiDrillExpanded[g.key] && (g.subs.length > 0
                          ? g.subs.map((s2) => {
                            const sk = `${g.key}||${s2.key}`;
                            return (
                              <React.Fragment key={sk}>
                                <button
                                  type="button"
                                  onClick={() => setKpiDrillExpanded((p) => ({ ...p, [sk]: !p[sk] }))}
                                  className="w-full grid grid-cols-11 gap-2 px-3 py-1 bg-violet-50/40 border-t border-violet-50 text-[11px] items-center font-medium text-left hover:bg-violet-100/50 transition cursor-pointer"
                                  data-testid={`kpi-drill-subgrp-${sk}`}
                                >
                                  <div className="col-span-6 text-violet-800 truncate flex items-center gap-1 pl-5">
                                    <ChevronDown className={`w-3 h-3 shrink-0 text-violet-400 transition-transform ${kpiDrillExpanded[sk] ? '' : '-rotate-90'}`} />
                                    {s2.key} <span className="text-[10px] font-normal text-slate-500">· {s2.invoices.length} inv</span>
                                  </div>
                                  <div className="text-right text-violet-700 tabular-nums">{fmtInr(s2.total)}</div>
                                  <div className={`text-right tabular-nums ${s2.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(s2.balance)}</div>
                                  <div className="col-span-3" />
                                </button>
                                {kpiDrillExpanded[sk] && s2.invoices.map((inv) => renderKpiInvRow(inv, `-${sk}`))}
                              </React.Fragment>
                            );
                          })
                          : g.invoices.map((inv) => renderKpiInvRow(inv, `-${g.key}`)))}
                      </React.Fragment>
                    ))}
                  <div className="grid grid-cols-11 gap-2 px-3 py-2 bg-slate-50 border-t-2 border-slate-300 text-[11px] items-center font-bold" data-testid="kpi-drill-total-row">
                    <div className="col-span-6 text-slate-800">GRAND TOTAL <span className="text-[10px] font-normal text-slate-500">· {kpiDrill.invoices.length} inv</span></div>
                    <div className="text-right text-violet-700 tabular-nums">{fmtInr(kpiDrillTotals.total)}</div>
                    <div className={`text-right tabular-nums ${kpiDrillTotals.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(kpiDrillTotals.balance)}</div>
                    <div className="col-span-3" />
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ─── About / Explanation modal ─── */}
        <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="books-about-dialog">
            <DialogHeader>
              <DialogTitle>About Books Analytics</DialogTitle>
              <p className="text-[11px] text-slate-500 mt-1">How every number on this page is computed and where it comes from.</p>
            </DialogHeader>
            <div className="space-y-4 text-xs text-slate-700 leading-relaxed">
              <div>
                <p className="font-semibold text-slate-900 mb-1">Purpose</p>
                <p>A single pane for finance and ops to track receivables, cash collection, aging, and channel performance across every invoice raised in Zoho Books — grouped by salesperson, client type, top customers, and now individual payments received.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Where the data comes from</p>
                <p>Every KPI, chart and table on this page reads from the <b>zoho_invoices</b> + <b>zoho_payments</b> caches which mirror your Zoho Books org. Category = Zoho custom field <code>cf_branch_name</code>. Client type = <code>cf_client_type</code>. Salesperson = <code>salesperson_name</code>.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">KPI definitions</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li><b>Invoiced</b> = <code>bcy_total</code> summed across invoices in range (INR at Zoho&apos;s exchange rate).</li>
                  <li><b>Collected</b> = <code>payment_made × exchange_rate</code> — real cash received against those invoices.</li>
                  <li><b>Outstanding</b> = <code>bcy_balance</code> — the still-open portion.</li>
                  <li><b>Write-off</b> = <code>Invoiced − Collected − Outstanding</code>, i.e. money knocked off via credit notes.</li>
                  <li><b>Avg Days to Pay</b> = average of (payment date − invoice date) across fully-paid invoices.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Two &quot;Collected&quot; values — cash-flow vs revenue</p>
                <p>The Overview <b>Collections</b> column and the <b>Payments Received</b> tab measure cash received in a month, while the Revenue Report&apos;s <b>Payments</b> measures how much of the invoices raised in the window has been paid. They can differ — that&apos;s not a bug, they answer different questions:</p>
                <ul className="list-disc ml-4 space-y-0.5 mt-1">
                  <li><b>Revenue Report &quot;Payments&quot;</b> = SUM(payment_made) on invoices raised <em>in this window</em>. Answers &quot;how much of what I billed has been paid?&quot;</li>
                  <li><b>Payments &quot;Collected (Cash-in)&quot;</b> = SUM(amount_bcy) on payments <em>received in this window</em> — regardless of which invoice&apos;s period. Answers &quot;how much cash flowed in?&quot;</li>
                </ul>
                <p className="mt-1">The gap between them is typically last-year invoices being collected this year.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Reconciliation identity</p>
                <p className="font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1.5 inline-block">Invoiced = Collected + Write-off + Outstanding</p>
                <p className="mt-1 text-slate-500">holds at every row and at the grand total.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Voided invoices</p>
                <p>Invoices marked <b>void</b> in Zoho are <b>excluded</b> from every KPI, chart and breakdown on this dashboard so they never inflate revenue. The small <span className="inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Void</span> pill in the active-filters row lets you review them separately.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Currency handling</p>
                <p>Foreign-currency invoices and payments (e.g. USD) are converted to <b>INR base currency</b> at Zoho&apos;s stored <code>exchange_rate</code> before any total is computed. Non-INR rows in the payments list show both the original and INR amounts.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Zoho sync</p>
                <p>The colored freshness pill next to the header shows how stale the cache is (green &lt; 15 min · amber &lt; 1 hr · red older). Clicking <b>Sync from Zoho</b> runs an incremental sync of <b>invoices + payments received</b> using <code>last_modified_time</code> — typical cost 2-6 API calls. First-ever sync pulls the FY backlog (~20-40 calls). A 30-second cooldown prevents accidental hammering.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Tabs</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li><b>Overview</b> — monthly Billing vs Collections cohort table (current vs previous-invoice collections), client-type mix, aging snapshot.</li>
                  <li><b>AR Aging</b> — buckets (0-30 / 31-60 / 61-90 / 91+) with per-customer or per-salesperson drilldown.</li>
                  <li><b>By Salesperson</b> — table of Invoiced / Collected / Outstanding / Write-off per rep.</li>
                  <li><b>By Client Type</b> — Retail vs Corporate vs any other <code>cf_client_type</code> value.</li>
                  <li><b>Payments Received</b> — actual cash-in events by Mode / Category / Salesperson / Customer, plus a paginated list linking each payment to its applied invoices in Zoho.</li>
                  <li><b>Top Customers</b> — highest-value accounts by Invoiced or Outstanding.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Filters</p>
                <p>Date range (day / month / quarter / FY / specific month / custom), salesperson (multi-select with is-in / is-not-in / grouped), client type, status, customer search, and advanced field filters. Filters apply consistently to every KPI, chart and tab.</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        {/* ─── Voided invoices modal ─── */}
        <Dialog open={voidState.open} onOpenChange={(o) => setVoidState((s) => ({ ...s, open: o }))}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="books-void-dialog">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2"><Ban className="w-4 h-4 text-slate-500" /> Voided invoices ({voidState.count})</DialogTitle>
              <p className="text-[11px] text-slate-500 mt-1">
                These invoices were cancelled in Zoho Books. Their original value was <b>₹{Math.round(voidState.total_voided_value).toLocaleString('en-IN')}</b>. They are <b>excluded</b> from every KPI, chart and breakdown on this dashboard (Salesperson, Client Type, Top Customers, Aging) so numbers reflect only real activity.
              </p>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              <div className="border border-slate-200 rounded overflow-hidden">
                <div className="grid grid-cols-8 gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium sticky top-0">
                  <div className="col-span-2">Invoice</div><div>Date</div><div className="col-span-2">Customer</div><div>Category</div><div>Salesperson</div><div className="text-right">Original Value</div>
                </div>
                {voidState.invoices.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-400">No voided invoices in this date range.</div>
                ) : voidState.invoices.map((inv) => (
                  <a key={inv.invoice_id} href={`https://books.zoho.com/app/699857543/#/invoices/${inv.invoice_id}`} target="_blank" rel="noreferrer" className="grid grid-cols-8 gap-2 px-3 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50" data-testid={`books-void-row-${inv.invoice_id}`}>
                    <div className="col-span-2 font-medium text-violet-700 whitespace-nowrap">{inv.invoice_number || '—'} ↗</div>
                    <div className="text-slate-500">{inv.date}</div>
                    <div className="col-span-2 text-slate-700 truncate">{inv.customer_name}</div>
                    <div className="text-slate-500 truncate">{inv.cf_branch_name}</div>
                    <div className="text-slate-500 truncate">{inv.salesperson_name || '—'}</div>
                    <div className="text-right tabular-nums text-slate-400 line-through">₹{Math.round(inv.voided_value).toLocaleString('en-IN')}</div>
                  </a>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ─── Payments drilldown modal ─── */}
        <Dialog open={pmtDrill.open} onOpenChange={(o) => setPmtDrill((s) => ({ ...s, open: o }))}>
          <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="pmt-drill-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">{pmtDrill.title}</DialogTitle>
              <p className="text-[11px] text-slate-500 mt-1">{pmtDrill.subtitle}</p>
            </DialogHeader>
            <div className="flex items-center gap-1.5 pb-2 border-b border-slate-100">
              <Layers className="w-3 h-3 text-slate-400" />
              <span className="text-[10px] text-slate-500 mr-1">Group by</span>
              {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => { setPmtDrillGroup(v); setPmtDrillExpanded({}); }} data-testid={`pmt-drill-group-${v}`}
                  className={`text-[11px] px-2 py-1 rounded-md border transition ${pmtDrillGroup === v ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
              {pmtDrillGroup !== 'none' && (
                <>
                  <span className="w-px h-4 bg-slate-200 mx-1" />
                  <span className="text-[10px] text-slate-500">Then by</span>
                  {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type']].filter(([v]) => v === 'none' || v !== pmtDrillGroup).map(([v, l]) => (
                    <button key={v} type="button" onClick={() => { setPmtDrillGroup2(v); setPmtDrillExpanded({}); }} data-testid={`pmt-drill-group2-${v}`}
                      className={`text-[11px] px-2 py-1 rounded-md border transition ${pmtDrillGroup2 === v ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
                  ))}
                </>
              )}
              {pmtDrillGroup === 'category' && <span className="text-[10px] text-slate-400 ml-2">multi-category payments are pro-rated per category</span>}
            </div>
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              {pmtDrill.loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-violet-600" /></div>
              ) : pmtDrill.payments.length === 0 ? (
                <p className="text-xs text-slate-400 py-8 text-center">No payments match these filters.</p>
              ) : (
                <div className="bg-white border border-slate-200 rounded overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-medium sticky top-0">
                    <div>Date</div><div>Pmt #</div><div className="col-span-3">Customer</div><div className="col-span-2">Mode</div>
                    <div>Reference</div><div className="col-span-2">Applied to</div><div>Salesperson</div><div className="text-right">Amount</div>
                  </div>
                  {pmtDrillGroups === null
                    ? pmtDrill.payments.map((p) => renderPmtDrillRow(p, p.payment_id))
                    : pmtDrillGroups.map((g) => (
                      <React.Fragment key={g.key}>
                        <button
                          type="button"
                          onClick={() => setPmtDrillExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))}
                          className="w-full grid grid-cols-12 gap-2 px-3 py-1.5 bg-emerald-50/70 border-t border-emerald-100 text-[11px] items-center font-semibold text-left hover:bg-emerald-100/70 transition cursor-pointer"
                          data-testid={`pmt-drill-grp-${g.key}`}
                        >
                          <div className="col-span-9 text-emerald-900 truncate flex items-center gap-1">
                            <ChevronDown className={`w-3 h-3 shrink-0 text-emerald-500 transition-transform ${pmtDrillExpanded[g.key] ? '' : '-rotate-90'}`} />
                            {g.key} <span className="text-[10px] font-normal text-slate-500">· {g.payments.length} pmt(s)</span>
                          </div>
                          <div className="col-span-3 text-right text-emerald-800 tabular-nums">{fmtInr(g.amount)}</div>
                        </button>
                        {pmtDrillExpanded[g.key] && (g.subs.length > 0
                          ? g.subs.map((s2) => {
                            const sk = `${g.key}||${s2.key}`;
                            return (
                              <React.Fragment key={sk}>
                                <button
                                  type="button"
                                  onClick={() => setPmtDrillExpanded((p) => ({ ...p, [sk]: !p[sk] }))}
                                  className="w-full grid grid-cols-12 gap-2 px-3 py-1 bg-emerald-50/40 border-t border-emerald-50 text-[11px] items-center font-medium text-left hover:bg-emerald-100/50 transition cursor-pointer"
                                  data-testid={`pmt-drill-subgrp-${sk}`}
                                >
                                  <div className="col-span-9 text-emerald-800 truncate flex items-center gap-1 pl-5">
                                    <ChevronDown className={`w-3 h-3 shrink-0 text-emerald-400 transition-transform ${pmtDrillExpanded[sk] ? '' : '-rotate-90'}`} />
                                    {s2.key} <span className="text-[10px] font-normal text-slate-500">· {s2.payments.length} pmt(s)</span>
                                  </div>
                                  <div className="col-span-3 text-right text-emerald-700 tabular-nums">{fmtInr(s2.amount)}</div>
                                </button>
                                {pmtDrillExpanded[sk] && s2.payments.map((p, i) => renderPmtDrillRow(p, `${p.payment_id}-${sk}-${i}`))}
                              </React.Fragment>
                            );
                          })
                          : g.payments.map((p, i) => renderPmtDrillRow(p, `${p.payment_id}-${g.key}-${i}`)))}
                      </React.Fragment>
                    ))}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-t-2 border-slate-300 text-[11px] items-center font-bold" data-testid="pmt-drill-total-row">
                    <div className="col-span-9 text-slate-800">GRAND TOTAL <span className="text-[10px] font-normal text-slate-500">· {pmtDrill.payments.length} pmt(s)</span></div>
                    <div className="col-span-3 text-right text-emerald-700 tabular-nums">{fmtInr(pmtDrillTotal)}</div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
}
